import { Fragment, type CSSProperties, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConfig } from '@config/provider';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { ApiResponseError, buildOrgUrl, resolveBasePath } from '@app/utils/api';
import { formatDateTime, formatNumber, formatRelativeTime } from '@app/utils/format';
import { downloadBlobAsFile, downloadFlowChartAsPng } from '@app/utils/svgExport';
import type { ChartExportContent } from '@app/utils/svgExport';
import { DownloadIcon } from '@app/components/icons';
import type {
  FlowConversationsResponse as ApiFlowConversations,
  FlowConversationschema as ApiFlowConversation,
  FlowDelaySummaryschema as ApiFlowDelaySummary,
  FlowEndpoint as ApiFlowEndpoint,
  FlowEndpointsResponse as ApiFlowEndpoints,
  FlowExporterIdentityschema as ApiFlowExporterIdentity,
  FlowInterfaceTotals as ApiFlowInterfaceTotals,
  FlowInterfaceTotalsResponse as ApiFlowInterfaceTotalsResponse,
  FlowMetricschema as ApiFlowMetric,
  FlowOverviewResponse as ApiFlowOverview,
  FlowReport as ApiFlowReport,
  FlowReportCreateRequest as ApiFlowReportCreateRequest,
  FlowTimeSeriesMetadataschema as ApiFlowMetadata,
  FlowSamplingState as ApiFlowSamplingState,
  FlowService as ApiFlowService,
  FlowServicesResponse as ApiFlowServices,
  FlowSourceSummary as ApiFlowSource,
  FlowSourcesResponse as ApiFlowSources,
  FlowTimeBucketschema as ApiFlowTimeBucket,
  PageMetaschema as ApiPageMeta
} from '@reap/openapi-public-v1';
import type { SiteSummary } from '@app/types/org-console';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import { chartRateForBucket } from './flowChartBuckets';
import { createFlowWindow, normalizeFlowTimestamp, normalizeFlowWindow, type FlowWindow } from './flowTimeRange';

// The API surface is contract-owned. The app fetches JSON directly rather than
// using generated FromJSON helpers, so preserve ISO-8601 date strings at the
// transport boundary while deriving every other field from generated models.
type FlowAnalyticsPayload<T> = T extends Date
  ? string
  : T extends Array<infer Item>
    ? FlowAnalyticsPayload<Item>[]
    : T extends object
      ? { [Key in keyof T]: FlowAnalyticsPayload<T[Key]> }
      : T;

// Keep the existing response types generated so a regenerated client exposes
// response drift at compile time. The site breakdown extension below mirrors
// the new contract while this UI checkout still pins the released SDK.
type SamplingState = ApiFlowSamplingState;
type FlowMetadata = FlowAnalyticsPayload<ApiFlowMetadata>;
type FlowQualityMetadata = Pick<FlowMetadata, 'effectiveStartTime' | 'effectiveEndTime' | 'samplingState' | 'requestId'>;
type FlowMetric = FlowAnalyticsPayload<ApiFlowMetric>;
type FlowExporterIdentity = FlowAnalyticsPayload<ApiFlowExporterIdentity>;
type FlowDelaySummary = FlowAnalyticsPayload<ApiFlowDelaySummary>;
type FlowTimeBucket = FlowAnalyticsPayload<ApiFlowTimeBucket>;
type PageMeta = FlowAnalyticsPayload<ApiPageMeta>;
// This extension mirrors FlowSiteTraffic in reap-contracts until the UI's
// pinned SDK advances to that contract revision.
type FlowSiteTraffic = {
  siteId: string;
  bytes: number;
  packets: number;
  recordCount: number;
  observedBytesPerSecond: number;
  observedPacketsPerSecond: number;
  trafficSharePercent: number;
};
type FlowOverview = FlowAnalyticsPayload<ApiFlowOverview> & { siteBreakdown?: FlowSiteTraffic[] };
type FlowReport = FlowAnalyticsPayload<ApiFlowReport>;
type FlowConversation = FlowAnalyticsPayload<ApiFlowConversation>;
type FlowConversations = FlowAnalyticsPayload<ApiFlowConversations>;
type FlowEndpoint = FlowAnalyticsPayload<ApiFlowEndpoint>;
type FlowEndpoints = FlowAnalyticsPayload<ApiFlowEndpoints>;
type FlowService = FlowAnalyticsPayload<ApiFlowService>;
type FlowServices = FlowAnalyticsPayload<ApiFlowServices>;
type FlowInterfaceTotals = FlowAnalyticsPayload<ApiFlowInterfaceTotals>;
type FlowInterfaceTotalsResponse = FlowAnalyticsPayload<ApiFlowInterfaceTotalsResponse>;
type FlowSource = FlowAnalyticsPayload<ApiFlowSource>;
type FlowSources = FlowAnalyticsPayload<ApiFlowSources>;
const makeDefaultWindow = createFlowWindow;

const toLocalInput = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const toISO = (value: string): string | null => {
  return normalizeFlowTimestamp(value);
};

const formatBytes = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let amount = value;
  let index = 0;
  while (amount >= 1024 && index < units.length - 1) {
    amount /= 1024;
    index += 1;
  }
  return (amount >= 10 || index === 0 ? amount.toFixed(0) : amount.toFixed(1)) + ' ' + units[index];
};

const formatProtocol = (value: string | undefined): string => value || 'Unknown';

const formatObservedRate = (bytesPerSecond: number | null | undefined): string => {
  if (bytesPerSecond == null || !Number.isFinite(bytesPerSecond)) return '—';
  const bitsPerSecond = bytesPerSecond * 8;
  if (bitsPerSecond >= 1_000_000) return (bitsPerSecond / 1_000_000).toFixed(1) + ' Mbps';
  if (bitsPerSecond >= 1_000) return (bitsPerSecond / 1_000).toFixed(1) + ' Kbps';
  return Math.round(bitsPerSecond) + ' bps';
};

const formatPacketRate = (packetsPerSecond: number | null | undefined): string => {
  if (packetsPerSecond == null || !Number.isFinite(packetsPerSecond)) return '—';
  if (packetsPerSecond >= 1_000_000) return (packetsPerSecond / 1_000_000).toFixed(1) + 'M pps';
  if (packetsPerSecond >= 1_000) return (packetsPerSecond / 1_000).toFixed(1) + 'K pps';
  return Math.round(packetsPerSecond) + ' pps';
};

const formatChartTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
};

const formatBucketDuration = (seconds: number | undefined): string => {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return 'Server-selected bucket';
  if (seconds === 60) return '1 minute';
  if (seconds % 60 === 0) return (seconds / 60) + ' minutes';
  return seconds + ' seconds';
};

const formatRangeDuration = (startTime: string, endTime: string): string => {
  const milliseconds = Date.parse(endTime) - Date.parse(startTime);
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '';
  const minutes = Math.round(milliseconds / 60_000);
  if (minutes < 60) return `${minutes}m`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const samplingStateLabel = (state: SamplingState): string => {
  switch (state) {
    case 'mixed': return 'Mixed sampling';
    case 'sampled': return 'Sampled';
    case 'unsampled': return 'Unsampled';
    default: return 'Sampling unknown';
  }
};

const trafficColors = ['#38bdf8', '#a78bfa', '#34d399', '#fbbf24', '#fb7185', '#94a3b8'];
const conversationLaneColors = ['#124f83', '#78acd2', '#c58b00', '#dec66f', '#546272', '#7c3aed', '#0891b2', '#db2777', '#10b981', '#f59e0b'];

const ChartDownloadButton = ({ svgRef, filename, label, content }: {
  svgRef: { current: SVGSVGElement | null };
  filename: string;
  label: string;
  content: ChartExportContent;
}) => {
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async (): Promise<void> => {
    if (!svgRef.current) return;
    setPreparing(true);
    setError(null);
    try {
      await downloadFlowChartAsPng(svgRef.current, filename, content);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The chart image could not be generated.');
    } finally {
      setPreparing(false);
    }
  };

  return <span className="flow-analytics__chart-download">
    <button type="button" className="flow-analytics__card-expand" onClick={() => { void download(); }} disabled={preparing} aria-label={`Download ${label} as PNG`} title={`Download ${label} as PNG`}>
      <DownloadIcon size={15} />
      <span className="sr-only">Download {label} as PNG</span>
    </button>
    {error ? <span className="sr-only" role="status">{error}</span> : null}
  </span>;
};

type ChartRate = number | null;

const observedChartRate = (
  bucket: FlowTimeBucket | undefined,
  index: number,
  bucketCount: number,
  timeSeriesResolution: string | undefined
): ChartRate => chartRateForBucket(bucket, index, bucketCount, timeSeriesResolution, bucket?.observedBytesPerSecond);

const chartLinePath = (values: ChartRate[], xFor: (index: number) => number, yFor: (value: number) => number): string => {
  let joinsPreviousPoint = false;
  return values.flatMap((value, index) => {
    if (value == null) {
      joinsPreviousPoint = false;
      return [];
    }
    const command = joinsPreviousPoint ? 'L' : 'M';
    joinsPreviousPoint = true;
    return `${command} ${xFor(index)} ${yFor(value)}`;
  }).join(' ');
};

const chartExportTimeAxisLabels = (timeline: string[]): string[] => timeline
  .filter((_time, index) => index === 0 || index === timeline.length - 1 || index % 5 === 0)
  .map(formatChartTime)
  .filter((time, index, values) => index === 0 || time !== values[index - 1]);

const TrafficTrend = ({ series, observedBytesPerSecond, observedPacketsPerSecond, metadata, scopeDescription, selectedRange, selectedOverview, selectionLoading, selectionError, onRangeSelect, onClearRange }: {
  series: FlowTimeBucket[];
  observedBytesPerSecond: number;
  observedPacketsPerSecond: number;
  metadata: FlowMetadata;
  scopeDescription: string;
  selectedRange: FlowWindow | null;
  selectedOverview: FlowOverview | null;
  selectionLoading: boolean;
  selectionError: string | null;
  onRangeSelect: (range: FlowWindow) => void;
  onClearRange: () => void;
}) => {
  const [rateUnit, setRateUnit] = useState<'traffic' | 'packets'>('traffic');
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [brush, setBrush] = useState<{ start: number; end: number } | null>(null);
  const brushRef = useRef<{ start: number; moved: boolean } | null>(null);
  const values = series.slice().sort((left, right) => left.startTime.localeCompare(right.startTime));
  const rateFor = (item: FlowTimeBucket): number => rateUnit === 'traffic' ? item.observedBytesPerSecond : item.observedPacketsPerSecond;
  const formatRate = rateUnit === 'traffic' ? formatObservedRate : formatPacketRate;
  const rateName = rateUnit === 'traffic' ? 'Traffic rate' : 'Packet rate';
  const chartRates = values.map((bucket, index) => chartRateForBucket(bucket, index, values.length, metadata.timeSeriesResolution, rateFor(bucket)));
  const observedIndexes = chartRates.flatMap((rate, index) => rate == null ? [] : [index]);
  if (values.length === 0 || observedIndexes.length === 0) return <p className="flow-analytics__message">No observed flow records were returned for this range. Empty buckets do not assert zero network traffic or complete source coverage.</p>;

  const width = 960;
  const height = 390;
  const padding = { top: 30, right: 74, bottom: 48, left: 66 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const maximumRate = Math.max(...observedIndexes.map((index) => chartRates[index]!), 1);
  const unroundedStep = maximumRate / 4;
  const magnitude = 10 ** Math.floor(Math.log10(unroundedStep));
  const normalizedStep = unroundedStep / magnitude;
  const niceStep = (normalizedStep <= 1 ? 1 : normalizedStep <= 2 ? 2 : normalizedStep <= 5 ? 5 : 10) * magnitude;
  const axisMaximum = Math.max(niceStep * 4, Math.ceil(maximumRate / niceStep) * niceStep);
  const axisTicks = Array.from({ length: Math.round(axisMaximum / niceStep) + 1 }, (_, index) => index * niceStep);
  const averageRate = rateUnit === 'traffic' ? observedBytesPerSecond : observedPacketsPerSecond;
  const xFor = (index: number) => padding.left + (index / Math.max(1, values.length - 1)) * plotWidth;
  const yForRate = (rate: number) => padding.top + (1 - rate / axisMaximum) * plotHeight;
  const yFor = (item: FlowTimeBucket) => yForRate(rateFor(item));
  const peakIndex = observedIndexes.reduce((largest, index) => chartRates[index]! > chartRates[largest]! ? index : largest, observedIndexes[0]!);
  const peak = values[peakIndex]!;
  const peakRate = chartRates[peakIndex]!;
  const peakLabelWidth = 108;
  const peakLabelHeight = 44;
  const peakLabelX = Math.max(padding.left, Math.min(width - padding.right - peakLabelWidth, xFor(peakIndex) - peakLabelWidth / 2));
  const peakLabelY = Math.max(2, yFor(peak) - peakLabelHeight - 15);
  const segments = values.reduce<number[][]>((current, _bucket, index) => {
    if (chartRates[index] == null) {
      if (current.at(-1)?.length) current.push([]);
      return current;
    }
    current.at(-1)!.push(index);
    return current;
  }, [[]]).filter((segment) => segment.length > 0);
  const selectedStartIndex = selectedRange == null ? -1 : values.findIndex((bucket) => bucket.startTime === selectedRange.startTime);
  const selectedEndIndex = selectedRange == null ? -1 : values.findIndex((bucket) => bucket.endTime === selectedRange.endTime);
  const persistedSelection = selectedStartIndex >= 0 && selectedEndIndex >= selectedStartIndex
    ? { start: selectedStartIndex, end: selectedEndIndex }
    : null;
  const activeIndex = brush ? brush.end : hoveredIndex;
  const activeBucket = activeIndex == null ? null : values[activeIndex] ?? null;
  const brushStart = brush == null ? persistedSelection?.start ?? null : Math.min(brush.start, brush.end);
  const brushEnd = brush == null ? persistedSelection?.end ?? null : Math.max(brush.start, brush.end);
  const brushWidth = brushStart == null || brushEnd == null ? 0 : Math.max(0, xFor(brushEnd) - xFor(brushStart));
  const gapSegments = observedIndexes.slice(1).flatMap((currentIndex, index) => {
    const previousIndex = observedIndexes[index]!;
    const previous = values[previousIndex]!;
    const bucket = values[currentIndex]!;
    return currentIndex - previousIndex > 1 ? [{ previous, bucket, previousIndex, currentIndex }] : [];
  });
  const chartPointAt = (svg: SVGSVGElement, clientX: number, clientY: number): DOMPoint | null => {
    const matrix = svg.getScreenCTM();
    if (!matrix) return null;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    return point.matrixTransform(matrix.inverse());
  };
  const indexAt = (chartX: number): number => Math.min(values.length - 1, Math.max(0, Math.round((chartX - padding.left) / plotWidth * (values.length - 1))));
  const updateHover = (svg: SVGSVGElement, clientX: number): number | null => {
    const point = chartPointAt(svg, clientX, 0);
    if (!point) return null;
    const index = indexAt(point.x);
    setHoveredIndex(index);
    return index;
  };
  const xTickCount = Math.min(7, values.length);
  const xTickIndexes = Array.from({ length: xTickCount }, (_, index) => Math.round(index * (values.length - 1) / Math.max(1, xTickCount - 1)))
    .filter((index, position, collection) => position === 0 || index !== collection[position - 1]);
  const activeRate = activeIndex == null ? null : chartRates[activeIndex] ?? null;
  const activePosition = activeIndex == null ? 50 : Math.max(12, Math.min(88, xFor(activeIndex) / width * 100));
  return (
    <figure className="flow-analytics__trend">
      <figcaption className="flow-analytics__trend-header">
        <div className="flow-analytics__trend-title">
          <h3>Traffic over time</h3>
          <p>{scopeDescription}</p>
        </div>
        <div className="flow-analytics__trend-badges" aria-label="Traffic chart data properties">
          <span className="flow-analytics__trend-badge flow-analytics__trend-badge--observed"><i aria-hidden="true" />Observed</span>
          <span className="flow-analytics__trend-badge flow-analytics__trend-badge--sampling">{samplingStateLabel(metadata.samplingState)}</span>
          <span className="flow-analytics__trend-badge">{metadata.timeSeriesResolution} resolution</span>
        </div>
      </figcaption>
      <div className="flow-analytics__trend-summary-row">
        <dl className="flow-analytics__trend-statistics">
          <div><dt>Avg</dt><dd>{formatRate(averageRate)}</dd></div>
          <div><dt>Peak</dt><dd>{formatRate(peakRate)}</dd><span>· {formatChartTime(peak.startTime)}</span></div>
        </dl>
        <div className="flow-analytics__trend-toggle" role="tablist" aria-label="Traffic over time metric">
          <button type="button" role="tab" aria-selected={rateUnit === 'traffic'} onClick={() => setRateUnit('traffic')}>Traffic rate</button>
          <button type="button" role="tab" aria-selected={rateUnit === 'packets'} onClick={() => setRateUnit('packets')}>Packet rate</button>
        </div>
      </div>
      <div className="flow-analytics__trend-chart">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          tabIndex={0}
          aria-label={`${rateName} by event-time bucket. Hover for bucket details or drag across the chart for a server-calculated range summary. The dashboard query remains unchanged.`}
          onFocus={() => setHoveredIndex((index) => index ?? 0)}
          onBlur={() => { if (!brushRef.current) setHoveredIndex(null); }}
          onPointerEnter={(event) => { if (!brushRef.current) updateHover(event.currentTarget, event.clientX); }}
          onPointerDown={(event) => {
            const point = chartPointAt(event.currentTarget, event.clientX, event.clientY);
            if (!point || point.y < padding.top || point.y > height - padding.bottom) return;
            const index = indexAt(point.x);
            event.currentTarget.setPointerCapture(event.pointerId);
            brushRef.current = { start: index, moved: false };
            setBrush({ start: index, end: index });
          }}
          onPointerMove={(event) => {
            const point = chartPointAt(event.currentTarget, event.clientX, event.clientY);
            if (!point) return;
            const index = indexAt(point.x);
            if (brushRef.current) {
              brushRef.current.moved ||= index !== brushRef.current.start;
              setBrush({ start: brushRef.current.start, end: index });
              return;
            }
            setHoveredIndex(index);
          }}
          onPointerUp={(event) => {
            const point = chartPointAt(event.currentTarget, event.clientX, event.clientY);
            const index = point ? indexAt(point.x) : brushRef.current?.start;
            const selection = brushRef.current;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
            brushRef.current = null;
            setBrush(null);
            if (index == null || !selection) return;
            if (selection.moved) {
              const start = values[Math.min(selection.start, index)]!;
              const end = values[Math.max(selection.start, index)]!;
              onRangeSelect({ startTime: start.startTime, endTime: end.endTime });
              return;
            }
            setHoveredIndex(index);
          }}
          onPointerCancel={() => { brushRef.current = null; setBrush(null); }}
          onPointerLeave={() => { if (!brushRef.current) setHoveredIndex(null); }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') { event.preventDefault(); setHoveredIndex((index) => Math.max(0, (index ?? 0) - 1)); }
            if (event.key === 'ArrowRight') { event.preventDefault(); setHoveredIndex((index) => Math.min(values.length - 1, (index ?? 0) + 1)); }
          }}
        >
          <defs>
            <linearGradient id="flow-throughput-fill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.32" />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {axisTicks.map((tick) => <g key={tick}>
            <line x1={padding.left} x2={width - padding.right} y1={yForRate(tick)} y2={yForRate(tick)} className="flow-analytics__trend-grid" />
            <text x={padding.left - 10} y={yForRate(tick) + 5} textAnchor="end" className="flow-analytics__trend-axis-label">{formatRate(tick)}</text>
          </g>)}
          <line x1={padding.left} x2={width - padding.right} y1={yForRate(averageRate)} y2={yForRate(averageRate)} className="flow-analytics__trend-average-line" />
          <text x={width - padding.right + 8} y={yForRate(averageRate) - 6} className="flow-analytics__trend-average-label">Avg {formatRate(averageRate)}</text>
          {brushStart != null && brushEnd != null ? <rect className="flow-analytics__trend-brush" x={xFor(brushStart)} y={padding.top} width={Math.max(4, brushWidth)} height={plotHeight} /> : null}
          {gapSegments.map(({ previous, bucket, previousIndex, currentIndex }) => <line key={`${previous.startTime}-${bucket.startTime}`} x1={xFor(previousIndex)} y1={yFor(previous)} x2={xFor(currentIndex)} y2={yFor(bucket)} className="flow-analytics__trend-gap-line" />)}
          {segments.map((segment) => {
            const path = segment.map((index, pointIndex) => `${pointIndex === 0 ? 'M' : 'L'} ${xFor(index)} ${yFor(values[index]!)}`).join(' ');
            const area = segment.length < 2 ? '' : `${path} L ${xFor(segment.at(-1)!)} ${height - padding.bottom} L ${xFor(segment[0]!)} ${height - padding.bottom} Z`;
            return <g key={segment[0]}>{area ? <path d={area} className="flow-analytics__trend-area" /> : null}<path d={path} className="flow-analytics__trend-line" /></g>;
          })}
          <circle cx={xFor(peakIndex)} cy={yFor(peak)} r="4.8" className="flow-analytics__trend-peak" />
          <g className="flow-analytics__trend-peak-callout" aria-hidden="true">
            <line x1={xFor(peakIndex)} x2={xFor(peakIndex)} y1={yFor(peak) - 4} y2={peakLabelY + peakLabelHeight} />
            <rect x={peakLabelX} y={peakLabelY} width={peakLabelWidth} height={peakLabelHeight} rx="6" />
            <text x={peakLabelX + 8} y={peakLabelY + 17}>{formatRate(peakRate)}</text>
            <text x={peakLabelX + 8} y={peakLabelY + 34}>{formatChartTime(peak.startTime)}</text>
          </g>
          {activeBucket ? <g className="flow-analytics__trend-hover" aria-hidden="true"><line x1={xFor(activeIndex!)} x2={xFor(activeIndex!)} y1={padding.top} y2={height - padding.bottom} />{activeRate != null ? <circle cx={xFor(activeIndex!)} cy={yForRate(activeRate)} r="4.5" /> : null}</g> : null}
          {xTickIndexes.map((index) => <g key={values[index]!.startTime}>
            <line x1={xFor(index)} x2={xFor(index)} y1={height - padding.bottom} y2={height - padding.bottom + 5} className="flow-analytics__trend-axis" />
            <text x={xFor(index)} y={height - 13} textAnchor={index === 0 ? 'start' : index === values.length - 1 ? 'end' : 'middle'} className="flow-analytics__trend-axis-label">{formatChartTime(values[index]!.startTime)}</text>
          </g>)}
        </svg>
        {activeBucket ? <aside className="flow-analytics__trend-tooltip" role="status" style={{ left: `${activePosition}%` }}>
          <strong>{formatDateTime(activeBucket.startTime)} – {formatChartTime(activeBucket.endTime)}</strong>
          {activeRate != null ? <><span>{rateName} <b>{formatRate(activeRate)}</b></span><span>Observed bytes <b>{formatBytes(activeBucket.bytes)}</b></span><span>Observed packets <b>{formatNumber(activeBucket.packets)}</b></span></> : <span>No flow records were returned for this bucket. Coverage is not asserted.</span>}
          <span>Resolution <b>{formatBucketDuration(activeBucket.bucketDurationSeconds)}</b></span>
        </aside> : null}
      </div>
      {selectedRange ? <section className="flow-analytics__trend-selection" aria-live="polite">
        <div>
          <div className="flow-analytics__trend-selection-heading"><span className="flow-analytics__eyebrow">Selected range</span><button type="button" className="flow-analytics__trend-selection-clear" onClick={onClearRange}>Clear selection</button></div>
          <strong>{formatChartTime(selectedRange.startTime)} – {formatChartTime(selectedRange.endTime)}</strong>
        </div>
        {selectionLoading ? <span>Calculating selected range…</span> : selectionError ? <span>{selectionError}</span> : selectedOverview ? <dl>
          <div><dt>Observed traffic</dt><dd>{formatBytes(selectedOverview.totalBytes)}</dd></div>
          <div><dt>Average rate</dt><dd>{formatObservedRate(selectedOverview.observedBytesPerSecond)}</dd></div>
          <div><dt>Observed packets</dt><dd>{formatNumber(selectedOverview.totalPackets)}</dd></div>
        </dl> : <span>Selected-range calculation is unavailable.</span>}
      </section> : null}
      <footer className="flow-analytics__trend-footer">
        <div className="flow-analytics__trend-info">
          <button type="button" aria-expanded={isInfoOpen} aria-controls="flow-traffic-trend-info" onClick={() => setIsInfoOpen((open) => !open)}><span aria-hidden="true">i</span><span className="sr-only">About observed traffic and chart interactions</span></button>
          {isInfoOpen ? <aside id="flow-traffic-trend-info" className="flow-analytics__trend-info-popover" role="note"><p>Observed traffic may include the same traffic seen by multiple flow sources.</p><p>Drag across the chart for a server-calculated range summary without changing other dashboard panels. Dashed links connect observed samples across intervals with no returned records; they do not assert zero traffic or complete source coverage.</p></aside> : null}
        </div>
        <span>{formatChartTime(values[0]!.startTime)} – {formatChartTime(values.at(-1)!.endTime)} ({formatRangeDuration(values[0]!.startTime, values.at(-1)!.endTime)})</span>
      </footer>
    </figure>
  );
};

// Keep very small non-zero categories discoverable in the visual while the table and tooltip retain the exact server-calculated shares.
const protocolVisualShares = (items: FlowMetric[]): number[] => {
  const total = items.reduce((sum, item) => sum + item.trafficSharePercent, 0);
  const minimum = 1.5;
  const smallItems = items.filter((item) => item.trafficSharePercent < minimum);
  if (smallItems.length === 0) return items.map((item) => item.trafficSharePercent);
  if (total <= smallItems.length * minimum) return items.map(() => total / items.length);
  const largeTotal = total - smallItems.reduce((sum, item) => sum + item.trafficSharePercent, 0);
  const largeAvailable = total - smallItems.length * minimum;
  return items.map((item) => item.trafficSharePercent < minimum ? minimum : item.trafficSharePercent * largeAvailable / largeTotal);
};

const TrafficComposition = ({ items, totalBytes, window, metadata, scopeLabel }: {
  items: FlowMetric[];
  totalBytes: number;
  window: FlowWindow;
  metadata: FlowQualityMetadata;
  scopeLabel: string;
}) => {
  const [hoveredProtocol, setHoveredProtocol] = useState<string | null>(null);
  const [selectedProtocol, setSelectedProtocol] = useState<string | null>(null);
  const rows = items.filter((item) => item.bytes > 0).slice(0, 5);
  const visualShares = protocolVisualShares(rows);
  let offset = 0;
  const activeProtocol = hoveredProtocol ?? selectedProtocol;
  const activeItem = activeProtocol == null ? null : rows.find((item) => item.key === activeProtocol) ?? null;

  const toggleProtocol = (protocol: string): void => {
    setSelectedProtocol((current) => current === protocol ? null : protocol);
  };

  const activateByKeyboard = (event: KeyboardEvent<SVGGElement>, protocol: string): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggleProtocol(protocol);
  };

  return (
    <section className="flow-analytics__composition" aria-labelledby="flow-composition-title">
      <div className="flow-analytics__card-heading"><div><span className="flow-analytics__eyebrow">Traffic composition</span><h3 id="flow-composition-title">Observed protocols</h3></div><span className="flow-analytics__card-note" title="Derived from the observed IP protocol field; application names are not inferred.">Protocol breakdown</span></div>
      <div className="flow-analytics__composition-content">
        <svg className="flow-analytics__donut" viewBox="0 0 42 42" role="img" aria-label="Protocol distribution by observed bytes. Select a protocol segment to inspect it in this card.">
          <circle cx="21" cy="21" r="15.9155" fill="transparent" stroke="#e2e8f0" strokeWidth="5" />
          {rows.map((item, index) => {
            const percentage = visualShares[index] ?? 0;
            const segmentOffset = offset;
            offset += percentage;
            const isActive = activeProtocol === item.key;
            const isSelected = selectedProtocol === item.key;
            return <g
              key={item.key}
              className={`flow-analytics__composition-slice${isActive ? ' flow-analytics__composition-slice--active' : ''}${isSelected ? ' flow-analytics__composition-slice--selected' : ''}`}
              role="button"
              tabIndex={0}
              aria-label={`${formatProtocol(item.key)}: ${formatTrafficShare(item.trafficSharePercent)}, ${formatBytes(item.bytes)}, ${formatNumber(item.packets)} packets. ${isSelected ? 'Clear' : 'Show'} details in this card.`}
              aria-pressed={isSelected}
              onPointerEnter={() => setHoveredProtocol(item.key)}
              onPointerLeave={() => setHoveredProtocol((current) => current === item.key ? null : current)}
              onFocus={() => setHoveredProtocol(item.key)}
              onBlur={() => setHoveredProtocol((current) => current === item.key ? null : current)}
              onKeyDown={(event) => activateByKeyboard(event, item.key)}
              onClick={() => toggleProtocol(item.key)}
            ><circle cx="21" cy="21" r="15.9155" fill="transparent" stroke={trafficColors[index % trafficColors.length]} strokeWidth="5" strokeDasharray={`${percentage} ${100 - percentage}`} strokeDashoffset={-segmentOffset} transform="rotate(-90 21 21)" /></g>;
          })}
          <text x="21" y="20" textAnchor="middle" className="flow-analytics__donut-label">{formatBytes(totalBytes)}</text>
          <text x="21" y="25" textAnchor="middle" className="flow-analytics__donut-caption">observed</text>
        </svg>
        <div className="flow-analytics__composition-table">
          <div className="flow-analytics__composition-table-heading" aria-hidden="true"><span>Protocol</span><span>Share</span><span>Observed bytes</span><span>Observed packets</span></div>
          <ul className="flow-analytics__composition-list">
            {rows.map((item, index) => {
              const isActive = activeProtocol === item.key;
              const isSelected = selectedProtocol === item.key;
              return <li key={item.key} className={`${isActive ? 'flow-analytics__composition-row--active ' : ''}${isSelected ? 'flow-analytics__composition-row--selected' : ''}`.trim()}>
                <button type="button" aria-pressed={isSelected} onClick={() => toggleProtocol(item.key)} onPointerEnter={() => setHoveredProtocol(item.key)} onPointerLeave={() => setHoveredProtocol((current) => current === item.key ? null : current)} onFocus={() => setHoveredProtocol(item.key)} onBlur={() => setHoveredProtocol((current) => current === item.key ? null : current)}>
                  <span style={{ backgroundColor: trafficColors[index % trafficColors.length] }} aria-hidden="true" /><strong>{formatProtocol(item.key)}</strong><em>{formatTrafficShare(item.trafficSharePercent)}</em><b>{formatBytes(item.bytes)}</b><i>{formatNumber(item.packets)} pkts</i>
                </button>
              </li>;
            })}
          </ul>
        </div>
      </div>
      {activeItem ? <aside className="flow-analytics__composition-tooltip" role="status"><strong>{formatProtocol(activeItem.key)}</strong><span>Traffic share <b>{formatTrafficShare(activeItem.trafficSharePercent)}</b></span><span>Observed bytes <b>{formatBytes(activeItem.bytes)}</b></span><span>Observed packets <b>{formatNumber(activeItem.packets)}</b></span><span>Average rate <b>{formatObservedRate(activeItem.observedBytesPerSecond)}</b></span><span>Scope <b>{scopeLabel}</b></span><span>Time range <b>{formatChartTime(window.startTime)} – {formatChartTime(window.endTime)}</b></span><span>Sampling <b>{metadata.samplingState}</b></span></aside> : null}
      <p className="flow-analytics__card-disclosure">Protocol values are observed from flow records. Select a protocol to pin its details in this card; the dashboard query does not change. Very small non-zero categories receive a minimum visual slice, while the table shows exact server-calculated shares. Application names are not inferred by this view.</p>
    </section>
  );
};

const FlowSourceSnapshot = ({ items, expandedSource, interfaceContext, interfaceContextLoading, interfaceContextError, onExpandedSourceChange }: {
  items: FlowSource[];
  expandedSource: FlowSource | null;
  interfaceContext: FlowInterfaceTotals[] | null;
  interfaceContextLoading: boolean;
  interfaceContextError: string | null;
  onExpandedSourceChange: (source: FlowSource | null) => void;
}) => {
  const visibleItems = items.slice(0, 10);
  const sourceCountLabel = items.length > visibleItems.length
    ? `Showing ${visibleItems.length} of ${items.length} sources`
    : `${items.length} flow source${items.length === 1 ? '' : 's'}`;

  return (
    <section className="flow-analytics__source-snapshot" aria-labelledby="flow-source-snapshot-title">
      <div className="flow-analytics__card-heading"><div><span className="flow-analytics__eyebrow">Flow exporter</span><h3 id="flow-source-snapshot-title">Flow sources</h3></div><span className="flow-analytics__source-count">{sourceCountLabel}</span></div>
      <ul>
        {visibleItems.map((item) => {
          const isSelected = expandedSource?.sourceKey === item.sourceKey;
          return <li key={item.sourceKey} className={isSelected ? 'flow-analytics__source-item--selected' : ''}>
            <button type="button" className="flow-analytics__source-row" aria-expanded={isSelected} onClick={() => onExpandedSourceChange(isSelected ? null : item)}>
              <span className={`flow-analytics__source-state flow-analytics__source-state--${item.samplingState}`} aria-label={`${item.samplingState} sampling`} />
              <span className="flow-analytics__source-identity"><strong>{item.samplerAddress}</strong><span>{item.flowType} · OD {item.observationDomainId}</span></span>
              <em>{formatRelativeTime(item.lastSeenAt)}</em>
              <span className="flow-analytics__source-toggle" aria-hidden="true">{isSelected ? '⌄' : '›'}</span>
            </button>
            {isSelected ? <div className="flow-analytics__source-interface-details">
              <div><strong>Interfaces observed in flow records</strong><span>Traffic is aggregated from flow records by input and output interface. Interface names are shown when a current mapping is available.</span></div>
              {interfaceContextLoading ? <p>Loading interface observations…</p> : interfaceContextError ? <p>{interfaceContextError}</p> : interfaceContext && interfaceContext.length > 0 ? <table className="flow-analytics__source-interface-table"><thead><tr><th scope="col">Interface</th><th scope="col">Ingress</th><th scope="col">Egress</th></tr></thead><tbody>
                {interfaceContext.map((row) => <tr key={row.ifIndex}>
                  <td><strong>{row.interfaceName ?? 'Unknown interface'}</strong><span>ifIndex {row.ifIndex}</span></td>
                  <td>{formatBytes(row.ingressBytes)} · {formatNumber(row.ingressPackets)} pkt</td>
                  <td>{formatBytes(row.egressBytes)} · {formatNumber(row.egressPackets)} pkt</td>
                </tr>)}
              </tbody></table> : <p>No interface observations were returned for this source in the selected range.</p>}
              <p className="flow-analytics__source-detail-guidance">Sampling and sequence signals are observational diagnostics and may indicate data quality, not exporter health.</p>
            </div> : null}
          </li>;
        })}
      </ul>
    </section>
  );
};

const formatExporterIdentity = (value: FlowExporterIdentity): string =>
  (value.flowType || 'Unknown') + ' · ' + (value.samplerAddress || 'Unknown') + ' · OD ' + value.observationDomainId;

const flowExporterLabel = (value: FlowExporterIdentity): string =>
  value.samplerAddress ? 'Exporter ' + value.samplerAddress : 'Unknown exporter';

const interfaceLabel = (row: FlowInterfaceTotals): string => row.interfaceName ?? 'ifIndex ' + row.ifIndex;

const interfaceIdentifier = (row: FlowInterfaceTotals): string => row.interfaceName ? row.interfaceName + ' · ifIndex ' + row.ifIndex : 'Raw ifIndex ' + row.ifIndex;

const conversationPathInterfaceLabel = (name: string | undefined, ifIndex: number | undefined): string => {
  if (ifIndex === 0) return 'Unknown · ifIndex 0';
  return name ?? (ifIndex == null ? 'Unknown interface' : 'ifIndex ' + ifIndex);
};

const conversationPathDirectionLabel = (conversation: FlowConversation, direction: string): string =>
  direction === 'second_to_first'
    ? conversation.destinationAddress + ' → ' + conversation.sourceAddress
    : conversation.sourceAddress + ' → ' + conversation.destinationAddress;

const conversationKey = (row: FlowConversation): string => [
  row.exporter.flowType,
  row.exporter.samplerAddress,
  row.exporter.observationDomainId,
  row.mode,
  row.sourceAddress,
  row.sourcePort,
  row.destinationAddress,
  row.destinationPort,
  row.protocol
].join('-');

const endpointPairLabel = (row: FlowConversation): string => row.sourceAddress + ' ↔ ' + row.destinationAddress;

const endpointKey = (row: FlowEndpoint): string => row.address;

const serviceKey = (row: FlowService): string => row.label;

const seriesRowSelector = '[data-flow-analytics-series-row]';

// A pinned series is intentionally local to its chart card: it supports focused
// inspection without changing the dashboard query or the ranked table results.
const usePinnedChartSeries = () => {
  const [pinnedSeriesKey, setPinnedSeriesKey] = useState<string | null>(null);
  const cardRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (pinnedSeriesKey == null) return;
    const clearOnOutsidePointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && !cardRef.current?.contains(event.target)) setPinnedSeriesKey(null);
    };
    document.addEventListener('pointerdown', clearOnOutsidePointerDown);
    return () => document.removeEventListener('pointerdown', clearOnOutsidePointerDown);
  }, [pinnedSeriesKey]);

  const clearOnCardBackgroundPointerDown = (event: ReactPointerEvent<HTMLElement>): void => {
    if (event.target instanceof Element && event.target.closest(seriesRowSelector)) return;
    setPinnedSeriesKey(null);
  };

  return { cardRef, pinnedSeriesKey, setPinnedSeriesKey, clearOnCardBackgroundPointerDown };
};

const FlowAnalyticsExpandedModal = ({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  return <div className="flow-analytics__expanded-modal" role="dialog" aria-modal="true" aria-label={label}>
    <div className="flow-analytics__expanded-modal-backdrop" onClick={onClose} />
    <div className="flow-analytics__expanded-modal-panel">{children}</div>
  </div>;
};

const formatTrafficShare = (trafficSharePercent: number): string => {
  if (!Number.isFinite(trafficSharePercent)) return '—';
  return trafficSharePercent.toFixed(1) + '%';
};

const dimensionMetricStyle = (sharePercent: number): CSSProperties => ({
  '--flow-dimension-share': `${sharePercent}%`
} as CSSProperties);

const isUnavailableNextHop = (key: string): boolean => {
  const normalized = key.trim().toLowerCase();
  return normalized === '' || normalized === 'unknown' || normalized === 'unavailable';
};

const isZeroAsn = (key: string): boolean => key.trim() === '' || key.trim() === '0' || key.trim().toLowerCase() === 'unknown';

const MetricBars = ({ items, labelFor, emptyMessage, totalBytes, shareBasis, trailingValue = 'share', unit = 'bytes', maximum, showShare = true }: {
  items: FlowMetric[];
  labelFor?: (item: FlowMetric) => string;
  emptyMessage: string;
  totalBytes: number;
  shareBasis: string;
  trailingValue?: 'share' | 'packets';
  unit?: 'bytes' | 'packets';
  maximum?: number;
  showShare?: boolean;
}) => {
  if (items.length === 0) return <p className="flow-analytics__dimension-empty">{emptyMessage}</p>;
  return <ul className="flow-analytics__dimension-bars">
    {items.map((item) => {
      const label = (labelFor?.(item) ?? item.key) || 'Unknown';
      const metricValue = unit === 'bytes' ? formatBytes(item.bytes) : `${formatNumber(item.packets)} pkts`;
      const metricName = unit === 'bytes' ? 'bytes' : 'packets';
      const sharePercent = totalBytes > 0 ? Math.min(100, Math.max(0, item.bytes / totalBytes * 100)) : 0;
      return <li key={item.key} style={unit === 'bytes' ? dimensionMetricStyle(sharePercent) : undefined} title={`${label}: ${formatBytes(item.bytes)} observed bytes, ${formatNumber(item.packets)} observed packets, ${formatTrafficShare(sharePercent)} of ${shareBasis}.`}>
        <strong>{label}</strong>
        <span className="flow-analytics__dimension-bar" aria-hidden="true">{unit === 'bytes' ? <i /> : maximum && maximum > 0 ? <meter min="0" max={maximum} value={item.packets} /> : null}</span>
        <b>{metricValue}</b>
        {showShare ? <em>{trailingValue === 'packets' ? `${formatNumber(item.packets)} pkts` : formatTrafficShare(sharePercent)}</em> : <em aria-hidden="true" />}
        <span className="sr-only">{metricName}</span>
      </li>;
    })}
  </ul>;
};

const NetworkDimensions = ({ overview, interfaces, interfaceMeta, interfaceLoading, interfaceError, onPreviousInterfacePage, onNextInterfacePage }: {
  overview: FlowOverview;
  interfaces: FlowInterfaceTotals[] | null;
  interfaceMeta: PageMeta | null;
  interfaceLoading: boolean;
  interfaceError: Error | null;
  onPreviousInterfacePage: () => void;
  onNextInterfacePage: () => void;
}) => {
  const [asnDirection, setAsnDirection] = useState<'source' | 'destination'>('source');
  const [interfaceUnit, setInterfaceUnit] = useState<'bytes' | 'packets'>('bytes');
  const asnRows = asnDirection === 'source' ? overview.topSourceAsns : overview.topDestinationAsns;
  const usableAsns = asnRows.filter((item) => !isZeroAsn(item.key));
  const unknownAsn = asnRows.find((item) => isZeroAsn(item.key));
  const knownNextHops = overview.topNextHops.filter((item) => !isUnavailableNextHop(item.key) && item.key !== '0.0.0.0');
  const interfaceMaximum = interfaceUnit === 'bytes' ? overview.totalBytes : overview.totalPackets;
  const interfaceValue = (item: FlowInterfaceTotals, direction: 'ingress' | 'egress'): number => {
    if (interfaceUnit === 'bytes') return direction === 'ingress' ? item.ingressBytes : item.egressBytes;
    return direction === 'ingress' ? item.ingressPackets : item.egressPackets;
  };
  const formatInterfaceValue = (value: number): string => interfaceUnit === 'bytes' ? formatBytes(value) : `${formatNumber(value)} pkts`;
  const interfaceTotal = (item: FlowInterfaceTotals): number => interfaceUnit === 'bytes' ? item.totalBytes : item.totalPackets;
  const nextHopCoverage = [
    { key: 'known', label: 'Known next hop', metric: overview.nextHopCoverage.known, color: '#2563eb' },
    { key: 'zero', label: '0.0.0.0', metric: overview.nextHopCoverage.zeroAddress, color: '#60a5fa' },
    { key: 'unavailable', label: 'Unknown / missing', metric: overview.nextHopCoverage.unavailable, color: '#f59e0b' }
  ];
  let nextHopOffset = 0;

  return <section className="flow-analytics__dimensions" aria-labelledby="flow-dimensions-title">
    <div className="flow-analytics__section-heading">
      <div><h2 id="flow-dimensions-title">Network dimensions</h2><p>Observed network context for the selected event-time range. Selecting a local tab changes only this card.</p></div>
    </div>
    <div className="flow-analytics__dimensions-grid">
      <div className="flow-analytics__dimensions-column">
      <section className="flow-analytics__dimension-card" aria-labelledby="flow-next-hops-title">
        <div className="flow-analytics__dimension-heading"><div><h3 id="flow-next-hops-title">Next hops</h3><p>Exporter-reported next-hop values across all observed flow records.</p></div><span>Observed bytes</span></div>
        <div className="flow-analytics__next-hop-content">
          <svg className="flow-analytics__next-hop-donut" viewBox="0 0 42 42" role="img" aria-label="Next-hop coverage by observed bytes.">
            <circle cx="21" cy="21" r="15.9155" fill="transparent" stroke="#e2e8f0" strokeWidth="5" />
            {nextHopCoverage.map((item) => {
              const share = item.metric.trafficSharePercent;
              const offset = nextHopOffset;
              nextHopOffset += share;
              return <circle key={item.key} cx="21" cy="21" r="15.9155" fill="transparent" stroke={item.color} strokeWidth="5" strokeDasharray={`${share} ${100 - share}`} strokeDashoffset={-offset} transform="rotate(-90 21 21)" />;
            })}
            <text x="21" y="20" textAnchor="middle" className="flow-analytics__donut-label">{formatBytes(overview.totalBytes)}</text>
            <text x="21" y="25" textAnchor="middle" className="flow-analytics__donut-caption">total bytes</text>
          </svg>
          <ul className="flow-analytics__next-hop-coverage">
            {nextHopCoverage.map((item) => <li key={item.key} title={`${item.label}: ${formatBytes(item.metric.bytes)} observed bytes, ${formatNumber(item.metric.packets)} observed packets, ${formatTrafficShare(item.metric.trafficSharePercent)} of observed traffic.`}><span style={{ backgroundColor: item.color }} aria-hidden="true" /><strong>{item.label}</strong><b>{formatBytes(item.metric.bytes)}</b><em>{formatTrafficShare(item.metric.trafficSharePercent)}</em></li>)}
          </ul>
        </div>
        <h4>Top known next hops</h4>
        <div className="flow-analytics__dimension-table-heading flow-analytics__dimension-table-heading--next-hop" aria-hidden="true"><span>Next hop</span><span>Observed bytes (% of known)</span><span>Bytes</span><span>Packets</span></div>
        <MetricBars items={knownNextHops} emptyMessage="No known next-hop addresses were returned for this interval." totalBytes={overview.nextHopCoverage.known.bytes} shareBasis="known next-hop bytes" trailingValue="packets" />
      </section>

      <section className="flow-analytics__dimension-card" aria-labelledby="flow-asns-title">
        <div className="flow-analytics__dimension-heading"><div><h3 id="flow-asns-title">Autonomous systems (ASNs)</h3><p>Source and destination ASN values when present in flow records.</p></div></div>
        <div className="flow-analytics__dimension-tabs" role="tablist" aria-label="Autonomous system direction">
          <button type="button" role="tab" aria-selected={asnDirection === 'source'} onClick={() => setAsnDirection('source')}>Source ASN</button>
          <button type="button" role="tab" aria-selected={asnDirection === 'destination'} onClick={() => setAsnDirection('destination')}>Destination ASN</button>
        </div>
        {usableAsns.length > 0
          ? <><div className="flow-analytics__dimension-table-heading" aria-hidden="true"><span>ASN</span><span>Observed bytes</span><span>Bytes</span><span>% of total</span></div><MetricBars items={usableAsns} labelFor={(item) => `AS${item.key}`} emptyMessage="No usable ASN values were returned for this interval." totalBytes={overview.totalBytes} shareBasis="total observed bytes" /></>
          : <div className="flow-analytics__asn-empty"><strong>No meaningful {asnDirection} ASN data.</strong><span>The observed traffic does not contain usable {asnDirection} ASN information for this time range.</span><p>Observed traffic without usable ASN: {unknownAsn ? `${formatBytes(unknownAsn.bytes)} · ${formatNumber(unknownAsn.packets)} pkts` : 'not reported'}.</p></div>}
      </section>
      </div>

      <div className="flow-analytics__dimensions-column">
      <section className="flow-analytics__dimension-card flow-analytics__interface-dimension" aria-labelledby="flow-interfaces-title">
        <div className="flow-analytics__dimension-heading"><div><h3 id="flow-interfaces-title">Interface traffic</h3><p>Ingress and egress observations by exporter and interface. Sorted by in + out observations.</p></div><div className="flow-analytics__dimension-tabs" role="tablist" aria-label="Interface traffic unit"><button type="button" role="tab" aria-selected={interfaceUnit === 'bytes'} onClick={() => setInterfaceUnit('bytes')}>Bytes</button><button type="button" role="tab" aria-selected={interfaceUnit === 'packets'} onClick={() => setInterfaceUnit('packets')}>Packets</button></div></div>
        {interfaceLoading ? <p className="flow-analytics__dimension-empty">Loading interface observations…</p> : interfaceError ? <p className="flow-analytics__dimension-empty flow-analytics__message--error">Unable to load interface observations: {interfaceError.message}</p> : interfaces && interfaces.length > 0 ? <>
          <div className="flow-analytics__interface-columns" aria-hidden="true"><span>Exporter / interface</span><span>Ingress</span><span /><span>Egress</span><span>In + out</span></div>
          <ul className="flow-analytics__interface-bars">
            {interfaces.map((item) => {
              const ingress = interfaceValue(item, 'ingress');
              const egress = interfaceValue(item, 'egress');
              const total = interfaceTotal(item);
              const name = interfaceLabel(item);
              const exporter = item.exporter.samplerAddress || 'Unknown exporter';
              return <li key={[item.exporter.flowType, item.exporter.samplerAddress, item.exporter.observationDomainId, item.ifIndex].join(':')} title={`${name} on ${exporter}. Ingress: ${formatInterfaceValue(ingress)}. Egress: ${formatInterfaceValue(egress)}. Total directional observations: ${formatInterfaceValue(total)}.`}>
                <div className="flow-analytics__interface-name"><strong>{exporter}</strong><span>{interfaceIdentifier(item)}</span></div>
                <b className="flow-analytics__interface-value flow-analytics__interface-value--ingress">{formatInterfaceValue(ingress)}</b>
                <div className="flow-analytics__interface-balance" aria-hidden="true"><meter className="flow-analytics__interface-meter--ingress" min="0" max={interfaceMaximum || 1} value={ingress} /><i /><meter className="flow-analytics__interface-meter--egress" min="0" max={interfaceMaximum || 1} value={egress} /></div>
                <b className="flow-analytics__interface-value flow-analytics__interface-value--egress">{formatInterfaceValue(egress)}</b>
                <b className="flow-analytics__interface-total">{formatInterfaceValue(total)}</b>
              </li>;
            })}
          </ul>
          {interfaceMeta ? <PageNavigation meta={interfaceMeta} onPrevious={onPreviousInterfacePage} onNext={onNextInterfacePage} /> : null}
        </> : <p className="flow-analytics__dimension-empty">No interface observations were returned for this interval.</p>}
      </section>

      </div>
    </div>
  </section>;
};

const ConversationPulse = ({ items, timeSeries, timeSeriesResolution, expanded = false, onClose }: {
  items: FlowConversation[];
  timeSeries: FlowTimeBucket[];
  timeSeriesResolution: string | undefined;
  expanded?: boolean;
  onClose?: () => void;
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredPointerY, setHoveredPointerY] = useState<number | null>(null);
  const [highlightedConversationKey, setHighlightedConversationKey] = useState<string | null>(null);
  const { cardRef, pinnedSeriesKey, setPinnedSeriesKey, clearOnCardBackgroundPointerDown } = usePinnedChartSeries();
  const [isExpanded, setIsExpanded] = useState(false);
  const [conversationChartWidth, setConversationChartWidth] = useState(44);
  const [isConversationSplitDragging, setIsConversationSplitDragging] = useState(false);
  const [viewport, setViewport] = useState<{ start: number; end: number | null }>({ start: 0, end: null });
  const [rangeInteraction, setRangeInteraction] = useState<'start' | 'end' | 'window' | null>(null);
  const [expandedConversationKey, setExpandedConversationKey] = useState<string | null>(null);
  const conversationWorkspaceRef = useRef<HTMLDivElement>(null);
  const rangeDragRef = useRef<{ type: 'start' | 'end' | 'window'; pointerIndex: number; start: number; end: number } | null>(null);
  const conversationSvgRef = useRef<SVGSVGElement>(null);
  const conversations = items.slice(0, 10).filter((item) => item.timeSeries.length > 0);
  const timeline = timeSeries.map((bucket) => bucket.startTime);
  if (conversations.length === 0 || timeline.length === 0) return null;

  const values = conversations.map((conversation) => {
    const buckets = new Map(conversation.timeSeries.map((bucket) => [bucket.startTime, bucket]));
    return timeline.map((time, index) => observedChartRate(buckets.get(time), index, timeSeries.length, timeSeriesResolution));
  });
  const totals = new Map(timeSeries.map((bucket) => [bucket.startTime, bucket]));
  // The expanded dialog is intentionally much wider than the compact card.
  // Its wider view box preserves the chart's aspect ratio without horizontal
  // letterboxing, while the compact card retains its existing dimensions.
  const width = expanded ? 2000 : 960;
  const height = 540;
  const padding = { top: 32, right: 24, bottom: 82, left: 100 };
  const plotWidth = width - padding.left - padding.right;
  const finalTimelineIndex = timeline.length - 1;
  const minimumRange = Math.min(2, finalTimelineIndex);
  const rangeEnd = Math.max(minimumRange, Math.min(viewport.end ?? finalTimelineIndex, finalTimelineIndex));
  const rangeStart = Math.min(Math.max(0, viewport.start), Math.max(0, rangeEnd - minimumRange));
  const displayTimeline = timeline.slice(rangeStart, rangeEnd + 1);
  const displayValues = values.map((series) => series.slice(rangeStart, rangeEnd + 1));
  const displayTotals = displayTimeline.map((time, index) => observedChartRate(totals.get(time), rangeStart + index, timeSeries.length, timeSeriesResolution));
  const pinnedConversationKey = pinnedSeriesKey != null && conversations.some((conversation) => conversationKey(conversation) === pinnedSeriesKey)
    ? pinnedSeriesKey
    : null;
  const displayedRates = (pinnedConversationKey == null
    ? displayValues
    : displayValues.filter((_, index) => conversationKey(conversations[index]!) === pinnedConversationKey))
    .flat()
    .filter((rate): rate is number => rate != null);
  const maxRate = Math.max(...displayedRates, 1);
  const xFor = (index: number) => padding.left + index / Math.max(1, displayTimeline.length - 1) * plotWidth;
  const yFor = (value: number) => padding.top + (1 - value / maxRate) * (height - padding.top - padding.bottom);
  const sliderXFor = (index: number) => padding.left + index / Math.max(1, finalTimelineIndex) * plotWidth;
  const lines = values.map((_, index) => ({
    color: conversationLaneColors[index % conversationLaneColors.length],
    key: conversationKey(conversations[index]!),
    path: chartLinePath(displayValues[index]!, xFor, yFor)
  }));
  const visibleLines = pinnedConversationKey == null ? lines : lines.filter((line) => line.key === pinnedConversationKey);
  const activeConversationKey = pinnedConversationKey ?? highlightedConversationKey;
  const axisTicks = [1, 0.75, 0.5, 0.25, 0];
  const hoveredConversations = hoveredIndex == null ? [] : conversations.map((conversation, index) => ({
      rank: index + 1,
      bucket: conversation.timeSeries.find((bucket) => bucket.startTime === displayTimeline[hoveredIndex]),
      conversation,
      rate: displayValues[index]?.[hoveredIndex] ?? null,
      color: conversationLaneColors[index % conversationLaneColors.length]
    })).filter(({ conversation }) => pinnedConversationKey == null || conversationKey(conversation) === pinnedConversationKey);
  const hovered = hoveredIndex == null ? null : {
    index: hoveredIndex,
    time: displayTimeline[hoveredIndex]!,
    total: pinnedConversationKey == null ? displayTotals[hoveredIndex] ?? null : hoveredConversations[0]?.rate ?? null,
    conversations: hoveredConversations
  };
  const hoverPosition = hovered == null ? 0 : xFor(hovered.index) / width * 100;
  const tooltipAlignment = hoverPosition > 68 ? ' flow-analytics__conversation-tooltip--end' : hoverPosition < 32 ? ' flow-analytics__conversation-tooltip--start' : '';
  const rangeControlTop = height - 58;
  const rangeControlBottom = height - 34;
  const minimumConversationChartWidth = 34;
  const maximumConversationChartWidth = 58;
  const chartPointAt = (svg: SVGSVGElement, clientX: number, clientY: number): DOMPoint | null => {
    const matrix = svg.getScreenCTM();
    if (!matrix) return null;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    return point.matrixTransform(matrix.inverse());
  };
  const sliderIndexAt = (chartX: number): number => {
    return Math.min(finalTimelineIndex, Math.max(0, Math.round((chartX - padding.left) / plotWidth * finalTimelineIndex)));
  };
  const selectBucketAt = (chartX: number, pointerY: number): void => {
    const index = Math.round((chartX - padding.left) / plotWidth * (displayTimeline.length - 1));
    setHoveredIndex(Math.min(displayTimeline.length - 1, Math.max(0, index)));
    setHoveredPointerY(Math.min(100, Math.max(0, pointerY)));
  };
  const updateViewportFromDrag = (nextIndex: number): void => {
    const drag = rangeDragRef.current;
    if (!drag) return;
    const rangeWidth = drag.end - drag.start;
    let nextStart = drag.start;
    let nextEnd = drag.end;
    if (drag.type === 'start') nextStart = Math.min(nextIndex, drag.end - minimumRange);
    if (drag.type === 'end') nextEnd = Math.max(nextIndex, drag.start + minimumRange);
    if (drag.type === 'window') {
      nextStart = Math.min(Math.max(0, drag.start + nextIndex - drag.pointerIndex), finalTimelineIndex - rangeWidth);
      nextEnd = nextStart + rangeWidth;
    }
    setViewport({ start: nextStart, end: nextEnd });
    setHoveredIndex(null);
    setHoveredPointerY(null);
  };
  const updateConversationSplit = (clientX: number): void => {
    const bounds = conversationWorkspaceRef.current?.getBoundingClientRect();
    if (!bounds || bounds.width <= 0) return;
    const nextWidth = (clientX - bounds.left) / bounds.width * 100;
    setConversationChartWidth(Math.round(Math.min(maximumConversationChartWidth, Math.max(minimumConversationChartWidth, nextWidth)) * 10) / 10);
  };
  const conversationSplitStyle = { '--flow-conversation-chart-width': `${conversationChartWidth}%` } as CSSProperties;
  const conversationTitleId = expanded ? 'flow-conversation-pulse-title-expanded' : 'flow-conversation-pulse-title';
  const conversationExportContent: ChartExportContent = {
    title: 'Top 10 conversations',
    subtitle: 'Endpoint pairs · observed traffic rate',
    columns: ['Conversation', 'Total bytes', 'Total pkts', 'Traffic share'],
    rows: conversations.map((conversation, index) => ({
      color: conversationLaneColors[index % conversationLaneColors.length],
      cells: [
        endpointPairLabel(conversation),
        formatBytes(conversation.bytes),
        formatNumber(conversation.packets),
        formatTrafficShare(conversation.trafficSharePercent)
      ]
    })),
    timeAxis: {
      labels: chartExportTimeAxisLabels(displayTimeline),
      plotLeft: padding.left,
      plotRight: padding.right,
      labelY: height - 28
    }
  };

  return (
    <>
    <section ref={cardRef} className={`flow-analytics__conversation-pulse${expanded ? ' flow-analytics__conversation-pulse--expanded' : ''}`} aria-labelledby={conversationTitleId} style={conversationSplitStyle} onPointerDown={clearOnCardBackgroundPointerDown}>
      <div className="flow-analytics__card-heading flow-analytics__conversation-heading">
        <div className="flow-analytics__conversation-chart-heading"><div><h3 id={conversationTitleId}>Top 10 conversations</h3><span className="flow-analytics__chart-subtitle">Endpoint pairs · observed traffic rate</span></div><div className="flow-analytics__chart-actions">{expanded ? <ChartDownloadButton svgRef={conversationSvgRef} filename="flow-top-conversations.png" label="Top conversations chart" content={conversationExportContent} /> : null}<button type="button" className="flow-analytics__card-expand" onClick={() => expanded ? onClose?.() : setIsExpanded(true)} aria-label={expanded ? 'Close expanded conversations' : 'Expand Top 10 conversations'}>{expanded ? 'Close' : <><span aria-hidden="true">⤢</span><span className="sr-only">Expand Top 10 conversations</span></>}</button></div></div>
        <span className="flow-analytics__conversation-split-gutter" aria-hidden="true" />
        <div className="flow-analytics__conversation-table-heading"><span className="flow-analytics__eyebrow">Endpoint-pair traffic totals</span><strong>Top 10 conversations</strong></div>
      </div>
      <div className="flow-analytics__conversation-workspace" ref={conversationWorkspaceRef}>
        <figure>
          <svg
          ref={conversationSvgRef}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          tabIndex={0}
          aria-label="Traffic-rate line chart with one line per top ten endpoint-pair conversation. Hover a conversation row or chart line to connect the two views. Drag either lower handle to resize the time range, or drag the highlighted range to slide it through time. Hover and use the left and right arrow keys to inspect a minute."
          onFocus={() => setHoveredIndex((index) => index ?? 0)}
          onBlur={() => { setHoveredIndex(null); setHoveredPointerY(null); }}
          onPointerEnter={(event) => {
            const chartPoint = chartPointAt(event.currentTarget, event.clientX, event.clientY);
            if (!chartPoint) return;
            const bounds = event.currentTarget.getBoundingClientRect();
            selectBucketAt(chartPoint.x, (event.clientY - bounds.top) / bounds.height * 100);
          }}
          onPointerDown={(event) => {
            const chartPoint = chartPointAt(event.currentTarget, event.clientX, event.clientY);
            if (!chartPoint || chartPoint.y < rangeControlTop - 8 || chartPoint.y > rangeControlBottom + 8) return;
            const nextIndex = sliderIndexAt(chartPoint.x);
            const handleHitRadius = 22;
            const distanceToStart = Math.abs(sliderXFor(nextIndex) - sliderXFor(rangeStart));
            const distanceToEnd = Math.abs(sliderXFor(nextIndex) - sliderXFor(rangeEnd));
            const type = distanceToStart <= handleHitRadius
              ? 'start'
              : distanceToEnd <= handleHitRadius
                ? 'end'
                : nextIndex >= rangeStart && nextIndex <= rangeEnd
                  ? 'window'
                  : null;
            if (!type) return;
            // Range manipulation is a chart interaction, not a request to clear
            // the selected table row.
            event.stopPropagation();
            event.currentTarget.setPointerCapture(event.pointerId);
            rangeDragRef.current = { type, pointerIndex: nextIndex, start: rangeStart, end: rangeEnd };
            setRangeInteraction(type);
            setHoveredIndex(null);
            setHoveredPointerY(null);
          }}
          onPointerMove={(event) => {
            const chartPoint = chartPointAt(event.currentTarget, event.clientX, event.clientY);
            if (!chartPoint) return;
            if (rangeDragRef.current) {
              updateViewportFromDrag(sliderIndexAt(chartPoint.x));
              return;
            }
            const bounds = event.currentTarget.getBoundingClientRect();
            selectBucketAt(chartPoint.x, (event.clientY - bounds.top) / bounds.height * 100);
          }}
          onPointerUp={(event) => {
            if (!rangeDragRef.current) return;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
            rangeDragRef.current = null;
            setRangeInteraction(null);
          }}
          onPointerCancel={() => { rangeDragRef.current = null; setRangeInteraction(null); setHoveredPointerY(null); }}
          onPointerLeave={() => { if (!rangeDragRef.current) { setHoveredIndex(null); setHoveredPointerY(null); setHighlightedConversationKey(null); } }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') { event.preventDefault(); setHoveredPointerY(null); setHoveredIndex((index) => Math.max(0, (index ?? 0) - 1)); }
            if (event.key === 'ArrowRight') { event.preventDefault(); setHoveredPointerY(null); setHoveredIndex((index) => Math.min(displayTimeline.length - 1, (index ?? 0) + 1)); }
          }}
        >
          {axisTicks.map((tick) => {
            const y = yFor(maxRate * tick);
            return <g key={tick}><line x1={padding.left} x2={width - padding.right} y1={y} y2={y} className="flow-analytics__conversation-grid" /><text x={padding.left - 12} y={y + 4} textAnchor="end">{formatObservedRate(maxRate * tick)}</text></g>;
          })}
          {visibleLines.map((line) => <path key={`${line.key}-hit-target`} d={line.path} className="flow-analytics__conversation-series-hit-target" onPointerEnter={() => setHighlightedConversationKey(line.key)} onPointerLeave={() => setHighlightedConversationKey(null)} />)}
          {visibleLines.map((line) => <path key={line.key} d={line.path} stroke={line.color} pointerEvents="none" className={`flow-analytics__conversation-series-line${activeConversationKey === line.key ? ' flow-analytics__conversation-series-line--highlighted' : activeConversationKey ? ' flow-analytics__conversation-series-line--muted' : ''}`} />)}
          {hovered ? <g className="flow-analytics__conversation-hover" aria-hidden="true" pointerEvents="none"><line x1={xFor(hovered.index)} x2={xFor(hovered.index)} y1={padding.top} y2={height - padding.bottom} />{hovered.conversations.map(({ rate, color, conversation }) => rate == null ? null : <circle key={conversationKey(conversation)} cx={xFor(hovered.index)} cy={yFor(rate)} r="4" stroke={color} />)}</g> : null}
          <g className={'flow-analytics__conversation-range-control' + (rangeInteraction ? ' flow-analytics__conversation-range-control--dragging' : '')} aria-hidden="true">
            <rect x={padding.left} y={height - 53} width={plotWidth} height="14" rx="3" />
            <rect x={sliderXFor(rangeStart) + 3} y={height - 50} width={Math.max(0, sliderXFor(rangeEnd) - sliderXFor(rangeStart) - 6)} height="8" rx="2" />
            <rect className="flow-analytics__conversation-range-window" x={sliderXFor(rangeStart) + 8} y={height - 56} width={Math.max(0, sliderXFor(rangeEnd) - sliderXFor(rangeStart) - 16)} height="20" rx="3" />
            <rect className="flow-analytics__conversation-range-handle" x={sliderXFor(rangeStart) - 11} y={height - 61} width="22" height="30" rx="3" />
            <rect className="flow-analytics__conversation-range-handle" x={sliderXFor(rangeEnd) - 11} y={height - 61} width="22" height="30" rx="3" />
            <path d={`M ${sliderXFor(rangeStart)} ${height - 58} v 24 M ${sliderXFor(rangeEnd)} ${height - 58} v 24`} />
          </g>
          <text className="flow-analytics__chart-range-time-label" x={padding.left} y={height - 14}>{formatChartTime(displayTimeline[0]!)}</text>
          <text className="flow-analytics__chart-range-time-label" x={width - padding.right} y={height - 14} textAnchor="end">{formatChartTime(displayTimeline.at(-1)!)}</text>
          </svg>
          {hovered ? <aside className={'flow-analytics__conversation-tooltip' + tooltipAlignment} style={hoveredPointerY == null ? { left: `${hoverPosition}%` } : hoveredPointerY < 50 ? { left: `${hoverPosition}%`, top: `calc(${hoveredPointerY}% + 1.25rem)` } : { left: `${hoverPosition}%`, top: 'auto', bottom: `calc(${100 - hoveredPointerY}% + 1.25rem)` }} role="status">
            <header><strong>{formatDateTime(hovered.time)}</strong><span>{formatObservedRate(hovered.total)} {pinnedConversationKey == null ? 'total' : 'selected'}</span></header>
            <ol>
              {hovered.conversations.map(({ conversation, rate, bucket, color, rank }) => <li key={conversationKey(conversation)}>
                <i style={{ backgroundColor: color }} />
                <div><strong>#{rank} {endpointPairLabel(conversation)}</strong><span>All observed ports and protocols · {conversation.exporter.flowType} {conversation.exporter.samplerAddress}</span></div>
                <em>{formatObservedRate(rate)}<small>{formatBytes(bucket?.recordCount ? bucket.bytes : null)} · {bucket?.recordCount ? formatNumber(bucket.packets) : '—'} pkt</small></em>
              </li>)}
            </ol>
          </aside> : null}
        </figure>
        <div
          className={`flow-analytics__conversation-splitter${isConversationSplitDragging ? ' flow-analytics__conversation-splitter--dragging' : ''}`}
          role="separator"
          aria-label="Resize chart and conversation table"
          aria-orientation="vertical"
          aria-valuemin={minimumConversationChartWidth}
          aria-valuemax={maximumConversationChartWidth}
          aria-valuenow={Math.round(conversationChartWidth)}
          aria-valuetext={`${Math.round(conversationChartWidth)}% chart, ${Math.round(100 - conversationChartWidth)}% table`}
          tabIndex={0}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            setIsConversationSplitDragging(true);
            updateConversationSplit(event.clientX);
          }}
          onPointerMove={(event) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) updateConversationSplit(event.clientX);
          }}
          onPointerUp={(event) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
            setIsConversationSplitDragging(false);
          }}
          onPointerCancel={() => setIsConversationSplitDragging(false)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') { event.preventDefault(); setConversationChartWidth((width) => Math.max(minimumConversationChartWidth, width - 2)); }
            if (event.key === 'ArrowRight') { event.preventDefault(); setConversationChartWidth((width) => Math.min(maximumConversationChartWidth, width + 2)); }
            if (event.key === 'Home') { event.preventDefault(); setConversationChartWidth(minimumConversationChartWidth); }
            if (event.key === 'End') { event.preventDefault(); setConversationChartWidth(maximumConversationChartWidth); }
          }}
        ><span aria-hidden="true" /></div>
        <aside className="flow-analytics__conversation-list" aria-label="Top ten conversations">
          <div className="flow-analytics__conversation-table-scroll">
            <table>
              <colgroup>
                <col className="flow-analytics__conversation-column" />
                <col className="flow-analytics__sent-bytes-column" />
                <col className="flow-analytics__sent-packets-column" />
                <col className="flow-analytics__traffic-share-column" />
              </colgroup>
              <thead><tr><th scope="col">Conversation</th><th scope="col">Total bytes</th><th scope="col">Total pkts</th><th scope="col">Traffic share</th></tr></thead>
              <tbody>
                {conversations.map((conversation, index) => {
                  const key = conversationKey(conversation);
                  const conversationExpanded = expandedConversationKey === key;
                  const directionalTraffic = conversation.directionalTraffic;
                  const directionalPaths = conversation.directionalPaths ?? [];
                  return <Fragment key={key}>
                    <tr data-flow-analytics-series-row className={`flow-analytics__conversation-row${conversationExpanded ? ' flow-analytics__conversation-row--expanded' : ''}${activeConversationKey === key ? ' flow-analytics__conversation-row--highlighted' : ''}`} onClick={() => setPinnedSeriesKey(key)} onPointerEnter={() => setHighlightedConversationKey(key)} onPointerLeave={() => setHighlightedConversationKey((current) => current === key ? null : current)}>
                      <td><button type="button" className="flow-analytics__conversation-toggle" aria-expanded={conversationExpanded} onFocus={() => setHighlightedConversationKey(key)} onBlur={() => setHighlightedConversationKey((current) => current === key ? null : current)} onClick={() => {
                        const nextExpanded = !conversationExpanded;
                        setExpandedConversationKey(nextExpanded ? key : null);
                      }}><i style={{ backgroundColor: conversationLaneColors[index % conversationLaneColors.length] }} /><span className="flow-analytics__conversation-rank">{index + 1}</span><span className="flow-analytics__conversation-disclosure" aria-hidden="true">{conversationExpanded ? '⌄' : '›'}</span><strong>{endpointPairLabel(conversation)}</strong></button></td>
                      <td>{formatBytes(conversation.bytes)}</td>
                      <td>{formatNumber(conversation.packets)}</td>
                      <td>{formatTrafficShare(conversation.trafficSharePercent)}</td>
                    </tr>
                    {conversationExpanded ? <tr className="flow-analytics__conversation-detail-row">
                      <td colSpan={4}>
                        <div className="flow-analytics__conversation-detail">
                          <div className="flow-analytics__conversation-observation-context">
                            <strong>Exporter {conversation.exporter.samplerAddress || 'Unknown exporter'} · {conversation.exporter.flowType || 'Unknown flow type'}</strong>
                          </div>
                          {directionalPaths.length > 0 ? <div className="flow-analytics__conversation-path-list">
                            <div className="flow-analytics__conversation-path-header" aria-hidden="true"><span>Direction</span><span>Observed on exporter<small>Input interface → Output interface</small></span><span>Bytes</span><span>Pkts</span></div>
                            {directionalPaths.map((path, pathIndex) => <div key={[path.direction, path.ingressIfIndex ?? '', path.egressIfIndex ?? '', pathIndex].join(':')} className="flow-analytics__conversation-path-row">
                              <strong>{conversationPathDirectionLabel(conversation, path.direction)}</strong>
                              <span>{conversationPathInterfaceLabel(path.ingressInterfaceName, path.ingressIfIndex)} <b aria-hidden="true">→</b> {conversationPathInterfaceLabel(path.egressInterfaceName, path.egressIfIndex)}</span>
                              <span>{formatBytes(path.bytes)}</span>
                              <span>{formatNumber(path.packets)}</span>
                            </div>)}
                          </div> : directionalTraffic ? <div className="flow-analytics__conversation-path-list">
                            <div className="flow-analytics__conversation-path-header" aria-hidden="true"><span>Direction</span><span>Observed on exporter<small>Input interface → Output interface</small></span><span>Bytes</span><span>Pkts</span></div>
                            <div className="flow-analytics__conversation-path-row"><strong>{conversation.sourceAddress} → {conversation.destinationAddress}</strong><span>Interface path unavailable</span><span>{formatBytes(directionalTraffic.firstToSecondBytes)}</span><span>{formatNumber(directionalTraffic.firstToSecondPackets)}</span></div>
                            <div className="flow-analytics__conversation-path-row"><strong>{conversation.destinationAddress} → {conversation.sourceAddress}</strong><span>Interface path unavailable</span><span>{formatBytes(directionalTraffic.secondToFirstBytes)}</span><span>{formatNumber(directionalTraffic.secondToFirstPackets)}</span></div>
                          </div> : <p className="flow-analytics__conversation-context-message">Directional observations were not supplied by the flow service.</p>}
                          {conversation.directionalPathsTruncated ? <p className="flow-analytics__conversation-context-message">Additional observed interface paths were omitted from this compact view.</p> : null}
                        </div>
                      </td>
                    </tr> : null}
                  </Fragment>;
                })}
              </tbody>
            </table>
          </div>
        </aside>
      </div>
      <p className="flow-analytics__card-disclosure">Each row ranks an exporter-scoped endpoint pair by total observed traffic across both directions, ports, and protocols. Click a row to show only its chart line; click elsewhere to restore every line. Expand a row to see the exporter-observed interface path for each direction; those counters describe the same flow records and are not additional traffic. Drag the lower handles to zoom the visible time window.</p>
    </section>
    {!expanded && isExpanded ? <FlowAnalyticsExpandedModal label="Top 10 conversations" onClose={() => setIsExpanded(false)}><ConversationPulse items={items} timeSeries={timeSeries} timeSeriesResolution={timeSeriesResolution} expanded onClose={() => setIsExpanded(false)} /></FlowAnalyticsExpandedModal> : null}
    </>
  );
};

const EndpointPulse = ({ items, timeSeries, timeSeriesResolution, expanded = false, onClose }: { items: FlowEndpoint[]; timeSeries: FlowTimeBucket[]; timeSeriesResolution: string | undefined; expanded?: boolean; onClose?: () => void }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredPointerY, setHoveredPointerY] = useState<number | null>(null);
  const [highlightedEndpointKey, setHighlightedEndpointKey] = useState<string | null>(null);
  const { cardRef, pinnedSeriesKey, setPinnedSeriesKey, clearOnCardBackgroundPointerDown } = usePinnedChartSeries();
  const [isExpanded, setIsExpanded] = useState(false);
  const [endpointChartWidth, setEndpointChartWidth] = useState(44);
  const [isEndpointSplitDragging, setIsEndpointSplitDragging] = useState(false);
  const [viewport, setViewport] = useState<{ start: number; end: number | null }>({ start: 0, end: null });
  const [rangeInteraction, setRangeInteraction] = useState<'start' | 'end' | 'window' | null>(null);
  const endpointWorkspaceRef = useRef<HTMLDivElement>(null);
  const rangeDragRef = useRef<{ type: 'start' | 'end' | 'window'; pointerIndex: number; start: number; end: number } | null>(null);
  const endpointSvgRef = useRef<SVGSVGElement>(null);
  const endpoints = items.slice(0, 10).filter((item) => item.timeSeries.length > 0);
  const timeline = timeSeries.map((bucket) => bucket.startTime);
  if (endpoints.length === 0 || timeline.length === 0) return null;

  const values = endpoints.map((endpoint) => {
    const buckets = new Map(endpoint.timeSeries.map((bucket) => [bucket.startTime, bucket]));
    return timeline.map((time, index) => observedChartRate(buckets.get(time), index, timeSeries.length, timeSeriesResolution));
  });
  const totals = new Map(timeSeries.map((bucket) => [bucket.startTime, bucket]));
  // The expanded dialog is intentionally much wider than the compact card.
  // Its wider view box preserves the chart's aspect ratio without horizontal
  // letterboxing, while the compact card retains its existing dimensions.
  const width = expanded ? 2000 : 960;
  const height = 540;
  const padding = { top: 32, right: 24, bottom: 82, left: 100 };
  const plotWidth = width - padding.left - padding.right;
  const finalTimelineIndex = timeline.length - 1;
  const minimumRange = Math.min(2, finalTimelineIndex);
  const rangeEnd = Math.max(minimumRange, Math.min(viewport.end ?? finalTimelineIndex, finalTimelineIndex));
  const rangeStart = Math.min(Math.max(0, viewport.start), Math.max(0, rangeEnd - minimumRange));
  const displayTimeline = timeline.slice(rangeStart, rangeEnd + 1);
  const displayValues = values.map((series) => series.slice(rangeStart, rangeEnd + 1));
  const displayTotals = displayTimeline.map((time, index) => observedChartRate(totals.get(time), rangeStart + index, timeSeries.length, timeSeriesResolution));
  const pinnedEndpointKey = pinnedSeriesKey != null && endpoints.some((endpoint) => endpointKey(endpoint) === pinnedSeriesKey)
    ? pinnedSeriesKey
    : null;
  const displayedRates = (pinnedEndpointKey == null
    ? displayValues
    : displayValues.filter((_, index) => endpointKey(endpoints[index]!) === pinnedEndpointKey))
    .flat()
    .filter((rate): rate is number => rate != null);
  const maxRate = Math.max(...displayedRates, 1);
  const xFor = (index: number) => padding.left + index / Math.max(1, displayTimeline.length - 1) * plotWidth;
  const yFor = (value: number) => padding.top + (1 - value / maxRate) * (height - padding.top - padding.bottom);
  const sliderXFor = (index: number) => padding.left + index / Math.max(1, finalTimelineIndex) * plotWidth;
  const lines = values.map((_, index) => ({
    color: conversationLaneColors[index % conversationLaneColors.length],
    key: endpointKey(endpoints[index]!),
    path: chartLinePath(displayValues[index]!, xFor, yFor)
  }));
  const visibleLines = pinnedEndpointKey == null ? lines : lines.filter((line) => line.key === pinnedEndpointKey);
  const activeEndpointKey = pinnedEndpointKey ?? highlightedEndpointKey;
  const axisTicks = [1, 0.75, 0.5, 0.25, 0];
  const hoveredEndpoints = hoveredIndex == null ? [] : endpoints.map((endpoint, index) => ({
      rank: index + 1,
      bucket: endpoint.timeSeries.find((bucket) => bucket.startTime === displayTimeline[hoveredIndex]),
      endpoint,
      rate: displayValues[index]?.[hoveredIndex] ?? null,
      color: conversationLaneColors[index % conversationLaneColors.length]
    })).filter(({ endpoint }) => pinnedEndpointKey == null || endpointKey(endpoint) === pinnedEndpointKey);
  const hovered = hoveredIndex == null ? null : {
    index: hoveredIndex,
    time: displayTimeline[hoveredIndex]!,
    total: pinnedEndpointKey == null ? displayTotals[hoveredIndex] ?? null : hoveredEndpoints[0]?.rate ?? null,
    endpoints: hoveredEndpoints
  };
  const hoverPosition = hovered == null ? 0 : xFor(hovered.index) / width * 100;
  const tooltipAlignment = hoverPosition > 68 ? ' flow-analytics__conversation-tooltip--end' : hoverPosition < 32 ? ' flow-analytics__conversation-tooltip--start' : '';
  const rangeControlTop = height - 58;
  const rangeControlBottom = height - 34;
  const minimumEndpointChartWidth = 34;
  const maximumEndpointChartWidth = 58;
  const chartPointAt = (svg: SVGSVGElement, clientX: number, clientY: number): DOMPoint | null => {
    const matrix = svg.getScreenCTM();
    if (!matrix) return null;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    return point.matrixTransform(matrix.inverse());
  };
  const sliderIndexAt = (chartX: number): number => Math.min(finalTimelineIndex, Math.max(0, Math.round((chartX - padding.left) / plotWidth * finalTimelineIndex)));
  const selectBucketAt = (chartX: number, pointerY: number): void => {
    const index = Math.round((chartX - padding.left) / plotWidth * (displayTimeline.length - 1));
    setHoveredIndex(Math.min(displayTimeline.length - 1, Math.max(0, index)));
    setHoveredPointerY(Math.min(100, Math.max(0, pointerY)));
  };
  const updateViewportFromDrag = (nextIndex: number): void => {
    const drag = rangeDragRef.current;
    if (!drag) return;
    const rangeWidth = drag.end - drag.start;
    let nextStart = drag.start;
    let nextEnd = drag.end;
    if (drag.type === 'start') nextStart = Math.min(nextIndex, drag.end - minimumRange);
    if (drag.type === 'end') nextEnd = Math.max(nextIndex, drag.start + minimumRange);
    if (drag.type === 'window') {
      nextStart = Math.min(Math.max(0, drag.start + nextIndex - drag.pointerIndex), finalTimelineIndex - rangeWidth);
      nextEnd = nextStart + rangeWidth;
    }
    setViewport({ start: nextStart, end: nextEnd });
    setHoveredIndex(null);
    setHoveredPointerY(null);
  };
  const updateEndpointSplit = (clientX: number): void => {
    const bounds = endpointWorkspaceRef.current?.getBoundingClientRect();
    if (!bounds || bounds.width <= 0) return;
    const nextWidth = (clientX - bounds.left) / bounds.width * 100;
    setEndpointChartWidth(Math.round(Math.min(maximumEndpointChartWidth, Math.max(minimumEndpointChartWidth, nextWidth)) * 10) / 10);
  };
  const endpointSplitStyle = { '--flow-conversation-chart-width': `${endpointChartWidth}%` } as CSSProperties;
  const endpointTitleId = expanded ? 'flow-endpoint-pulse-title-expanded' : 'flow-endpoint-pulse-title';
  const endpointExportContent: ChartExportContent = {
    title: 'Top 10 endpoints',
    subtitle: 'Endpoint addresses · total observed traffic rate',
    columns: ['Endpoint', 'Sent bytes', 'Received bytes', 'Sent pkts', 'Received pkts', 'Traffic share'],
    rows: endpoints.map((endpoint, index) => ({
      color: conversationLaneColors[index % conversationLaneColors.length],
      cells: [
        endpoint.address,
        formatBytes(endpoint.sentBytes),
        formatBytes(endpoint.receivedBytes),
        formatNumber(endpoint.sentPackets),
        formatNumber(endpoint.receivedPackets),
        formatTrafficShare(endpoint.trafficSharePercent)
      ]
    })),
    timeAxis: {
      labels: chartExportTimeAxisLabels(displayTimeline),
      plotLeft: padding.left,
      plotRight: padding.right,
      labelY: height - 28
    }
  };

  return (
    <>
    <section ref={cardRef} className={`flow-analytics__conversation-pulse flow-analytics__endpoint-pulse${expanded ? ' flow-analytics__conversation-pulse--expanded' : ''}`} aria-labelledby={endpointTitleId} style={endpointSplitStyle} onPointerDown={clearOnCardBackgroundPointerDown}>
      <div className="flow-analytics__card-heading flow-analytics__conversation-heading">
        <div className="flow-analytics__conversation-chart-heading"><div><h3 id={endpointTitleId}>Top 10 endpoints</h3><span className="flow-analytics__chart-subtitle">Endpoint addresses · total observed traffic rate</span></div><div className="flow-analytics__chart-actions">{expanded ? <ChartDownloadButton svgRef={endpointSvgRef} filename="flow-top-endpoints.png" label="Top endpoints chart" content={endpointExportContent} /> : null}<button type="button" className="flow-analytics__card-expand" onClick={() => expanded ? onClose?.() : setIsExpanded(true)} aria-label={expanded ? 'Close expanded endpoints' : 'Expand Top 10 endpoints'}>{expanded ? 'Close' : <><span aria-hidden="true">⤢</span><span className="sr-only">Expand Top 10 endpoints</span></>}</button></div></div>
        <span className="flow-analytics__conversation-split-gutter" aria-hidden="true" />
        <div className="flow-analytics__conversation-table-heading"><span className="flow-analytics__eyebrow">Ranked by total observed traffic · sent + received</span><strong>Top 10 endpoints</strong></div>
      </div>
      <div className="flow-analytics__conversation-workspace" ref={endpointWorkspaceRef}>
        <figure>
          <svg
            ref={endpointSvgRef}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            tabIndex={0}
            aria-label="Traffic-rate line chart with one line per top ten endpoint. Hover an endpoint row or chart line to connect the two views. Drag either lower handle to resize the time range, or drag the highlighted range to slide it through time. Hover and use the left and right arrow keys to inspect a minute."
            onFocus={() => setHoveredIndex((index) => index ?? 0)}
            onBlur={() => { setHoveredIndex(null); setHoveredPointerY(null); }}
            onPointerEnter={(event) => {
              const chartPoint = chartPointAt(event.currentTarget, event.clientX, event.clientY);
              if (!chartPoint) return;
              const bounds = event.currentTarget.getBoundingClientRect();
              selectBucketAt(chartPoint.x, (event.clientY - bounds.top) / bounds.height * 100);
            }}
            onPointerDown={(event) => {
              const chartPoint = chartPointAt(event.currentTarget, event.clientX, event.clientY);
              if (!chartPoint || chartPoint.y < rangeControlTop - 8 || chartPoint.y > rangeControlBottom + 8) return;
              const nextIndex = sliderIndexAt(chartPoint.x);
              const handleHitRadius = 22;
              const distanceToStart = Math.abs(sliderXFor(nextIndex) - sliderXFor(rangeStart));
              const distanceToEnd = Math.abs(sliderXFor(nextIndex) - sliderXFor(rangeEnd));
              const type = distanceToStart <= handleHitRadius ? 'start' : distanceToEnd <= handleHitRadius ? 'end' : nextIndex >= rangeStart && nextIndex <= rangeEnd ? 'window' : null;
              if (!type) return;
              // Preserve a selected endpoint while its chart range is adjusted.
              event.stopPropagation();
              event.currentTarget.setPointerCapture(event.pointerId);
              rangeDragRef.current = { type, pointerIndex: nextIndex, start: rangeStart, end: rangeEnd };
              setRangeInteraction(type);
              setHoveredIndex(null);
              setHoveredPointerY(null);
            }}
            onPointerMove={(event) => {
              const chartPoint = chartPointAt(event.currentTarget, event.clientX, event.clientY);
              if (!chartPoint) return;
              if (rangeDragRef.current) {
                updateViewportFromDrag(sliderIndexAt(chartPoint.x));
                return;
              }
              const bounds = event.currentTarget.getBoundingClientRect();
              selectBucketAt(chartPoint.x, (event.clientY - bounds.top) / bounds.height * 100);
            }}
            onPointerUp={(event) => {
              if (!rangeDragRef.current) return;
              if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
              rangeDragRef.current = null;
              setRangeInteraction(null);
            }}
            onPointerCancel={() => { rangeDragRef.current = null; setRangeInteraction(null); setHoveredPointerY(null); }}
            onPointerLeave={() => { if (!rangeDragRef.current) { setHoveredIndex(null); setHoveredPointerY(null); setHighlightedEndpointKey(null); } }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') { event.preventDefault(); setHoveredPointerY(null); setHoveredIndex((index) => Math.max(0, (index ?? 0) - 1)); }
              if (event.key === 'ArrowRight') { event.preventDefault(); setHoveredPointerY(null); setHoveredIndex((index) => Math.min(displayTimeline.length - 1, (index ?? 0) + 1)); }
            }}
          >
            {axisTicks.map((tick) => {
              const y = yFor(maxRate * tick);
              return <g key={tick}><line x1={padding.left} x2={width - padding.right} y1={y} y2={y} className="flow-analytics__conversation-grid" /><text x={padding.left - 12} y={y + 4} textAnchor="end">{formatObservedRate(maxRate * tick)}</text></g>;
            })}
            {visibleLines.map((line) => <path key={`${line.key}-hit-target`} d={line.path} className="flow-analytics__conversation-series-hit-target" onPointerEnter={() => setHighlightedEndpointKey(line.key)} onPointerLeave={() => setHighlightedEndpointKey(null)} />)}
            {visibleLines.map((line) => <path key={line.key} d={line.path} stroke={line.color} pointerEvents="none" className={`flow-analytics__conversation-series-line${activeEndpointKey === line.key ? ' flow-analytics__conversation-series-line--highlighted' : activeEndpointKey ? ' flow-analytics__conversation-series-line--muted' : ''}`} />)}
            {hovered ? <g className="flow-analytics__conversation-hover" aria-hidden="true" pointerEvents="none"><line x1={xFor(hovered.index)} x2={xFor(hovered.index)} y1={padding.top} y2={height - padding.bottom} />{hovered.endpoints.map(({ rate, color, endpoint }) => rate == null ? null : <circle key={endpointKey(endpoint)} cx={xFor(hovered.index)} cy={yFor(rate)} r="4" stroke={color} />)}</g> : null}
            <g className={'flow-analytics__conversation-range-control' + (rangeInteraction ? ' flow-analytics__conversation-range-control--dragging' : '')} aria-hidden="true">
              <rect x={padding.left} y={height - 53} width={plotWidth} height="14" rx="3" />
              <rect x={sliderXFor(rangeStart) + 3} y={height - 50} width={Math.max(0, sliderXFor(rangeEnd) - sliderXFor(rangeStart) - 6)} height="8" rx="2" />
              <rect className="flow-analytics__conversation-range-window" x={sliderXFor(rangeStart) + 8} y={height - 56} width={Math.max(0, sliderXFor(rangeEnd) - sliderXFor(rangeStart) - 16)} height="20" rx="3" />
              <rect className="flow-analytics__conversation-range-handle" x={sliderXFor(rangeStart) - 11} y={height - 61} width="22" height="30" rx="3" />
              <rect className="flow-analytics__conversation-range-handle" x={sliderXFor(rangeEnd) - 11} y={height - 61} width="22" height="30" rx="3" />
              <path d={`M ${sliderXFor(rangeStart)} ${height - 58} v 24 M ${sliderXFor(rangeEnd)} ${height - 58} v 24`} />
            </g>
          <text className="flow-analytics__chart-range-time-label" x={padding.left} y={height - 14}>{formatChartTime(displayTimeline[0]!)}</text>
          <text className="flow-analytics__chart-range-time-label" x={width - padding.right} y={height - 14} textAnchor="end">{formatChartTime(displayTimeline.at(-1)!)}</text>
          </svg>
          {hovered ? <aside className={'flow-analytics__conversation-tooltip' + tooltipAlignment} style={hoveredPointerY == null ? { left: `${hoverPosition}%` } : hoveredPointerY < 50 ? { left: `${hoverPosition}%`, top: `calc(${hoveredPointerY}% + 1.25rem)` } : { left: `${hoverPosition}%`, top: 'auto', bottom: `calc(${100 - hoveredPointerY}% + 1.25rem)` }} role="status">
            <header><strong>{formatDateTime(hovered.time)}</strong><span>{formatObservedRate(hovered.total)} {pinnedEndpointKey == null ? 'total' : 'selected'}</span></header>
            <ol>
              {hovered.endpoints.map(({ endpoint, rate, bucket, color, rank }) => <li key={endpointKey(endpoint)}>
                <i style={{ backgroundColor: color }} />
                <div><strong>#{rank} {endpoint.address}</strong><span>Observed endpoint traffic · sent and received</span></div>
                <em>{formatObservedRate(rate)}<small>{formatBytes(bucket?.recordCount ? bucket.bytes : null)} · {bucket?.recordCount ? formatNumber(bucket.packets) : '—'} pkt</small></em>
              </li>)}
            </ol>
          </aside> : null}
        </figure>
        <div
          className={`flow-analytics__conversation-splitter${isEndpointSplitDragging ? ' flow-analytics__conversation-splitter--dragging' : ''}`}
          role="separator"
          aria-label="Resize chart and endpoint table"
          aria-orientation="vertical"
          aria-valuemin={minimumEndpointChartWidth}
          aria-valuemax={maximumEndpointChartWidth}
          aria-valuenow={Math.round(endpointChartWidth)}
          aria-valuetext={`${Math.round(endpointChartWidth)}% chart, ${Math.round(100 - endpointChartWidth)}% table`}
          tabIndex={0}
          onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); setIsEndpointSplitDragging(true); updateEndpointSplit(event.clientX); }}
          onPointerMove={(event) => { if (event.currentTarget.hasPointerCapture(event.pointerId)) updateEndpointSplit(event.clientX); }}
          onPointerUp={(event) => { if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); setIsEndpointSplitDragging(false); }}
          onPointerCancel={() => setIsEndpointSplitDragging(false)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') { event.preventDefault(); setEndpointChartWidth((width) => Math.max(minimumEndpointChartWidth, width - 2)); }
            if (event.key === 'ArrowRight') { event.preventDefault(); setEndpointChartWidth((width) => Math.min(maximumEndpointChartWidth, width + 2)); }
            if (event.key === 'Home') { event.preventDefault(); setEndpointChartWidth(minimumEndpointChartWidth); }
            if (event.key === 'End') { event.preventDefault(); setEndpointChartWidth(maximumEndpointChartWidth); }
          }}
        ><span aria-hidden="true" /></div>
        <aside className="flow-analytics__conversation-list" aria-label="Top ten endpoints">
          <div className="flow-analytics__conversation-table-scroll">
            <table>
              <colgroup>
                <col className="flow-analytics__conversation-column" />
                <col className="flow-analytics__sent-bytes-column" />
                <col className="flow-analytics__received-bytes-column" />
                <col className="flow-analytics__sent-packets-column" />
                <col className="flow-analytics__received-packets-column" />
                <col className="flow-analytics__traffic-share-column" />
              </colgroup>
              <thead><tr><th scope="col">Endpoint</th><th scope="col">Sent bytes</th><th scope="col">Received bytes</th><th scope="col">Sent pkts</th><th scope="col">Received pkts</th><th scope="col">Traffic share</th></tr></thead>
              <tbody>
                {endpoints.map((endpoint, index) => {
                  const key = endpointKey(endpoint);
                  return <tr key={key} data-flow-analytics-series-row className={`flow-analytics__conversation-row${activeEndpointKey === key ? ' flow-analytics__conversation-row--highlighted' : ''}`} onClick={() => setPinnedSeriesKey(key)} onPointerEnter={() => setHighlightedEndpointKey(key)} onPointerLeave={() => setHighlightedEndpointKey((current) => current === key ? null : current)}>
                    <td><span className="flow-analytics__conversation-toggle"><i style={{ backgroundColor: conversationLaneColors[index % conversationLaneColors.length] }} /><span className="flow-analytics__conversation-rank">{index + 1}</span><span aria-hidden="true" /><strong>{endpoint.address}</strong></span></td>
                    <td>{formatBytes(endpoint.sentBytes)}</td>
                    <td>{formatBytes(endpoint.receivedBytes)}</td>
                    <td>{formatNumber(endpoint.sentPackets)}</td>
                    <td>{formatNumber(endpoint.receivedPackets)}</td>
                    <td>{formatTrafficShare(endpoint.trafficSharePercent)}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </aside>
      </div>
      <p className="flow-analytics__card-disclosure">Each endpoint is ranked by total observed traffic: sent is traffic where the address is the source, and received is traffic where it is the destination. Click a row to show only its chart line; click elsewhere to restore every line. Drag the lower handles to zoom the visible time window.</p>
    </section>
    {!expanded && isExpanded ? <FlowAnalyticsExpandedModal label="Top 10 endpoints" onClose={() => setIsExpanded(false)}><EndpointPulse items={items} timeSeries={timeSeries} timeSeriesResolution={timeSeriesResolution} expanded onClose={() => setIsExpanded(false)} /></FlowAnalyticsExpandedModal> : null}
    </>
  );
};

const ServicePulse = ({ items, timeSeries, timeSeriesResolution, expanded = false, onClose }: { items: FlowService[]; timeSeries: FlowTimeBucket[]; timeSeriesResolution: string | undefined; expanded?: boolean; onClose?: () => void }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredPointerY, setHoveredPointerY] = useState<number | null>(null);
  const [highlightedServiceKey, setHighlightedServiceKey] = useState<string | null>(null);
  const { cardRef, pinnedSeriesKey, setPinnedSeriesKey, clearOnCardBackgroundPointerDown } = usePinnedChartSeries();
  const [isExpanded, setIsExpanded] = useState(false);
  const [serviceChartWidth, setServiceChartWidth] = useState(44);
  const [isServiceSplitDragging, setIsServiceSplitDragging] = useState(false);
  const [viewport, setViewport] = useState<{ start: number; end: number | null }>({ start: 0, end: null });
  const [rangeInteraction, setRangeInteraction] = useState<'start' | 'end' | 'window' | null>(null);
  const serviceWorkspaceRef = useRef<HTMLDivElement>(null);
  const rangeDragRef = useRef<{ type: 'start' | 'end' | 'window'; pointerIndex: number; start: number; end: number } | null>(null);
  const serviceSvgRef = useRef<SVGSVGElement>(null);
  const services = items.slice(0, 10).filter((item) => item.timeSeries.length > 0);
  const timeline = timeSeries.map((bucket) => bucket.startTime);
  if (services.length === 0 || timeline.length === 0) return null;

  const values = services.map((service) => {
    const buckets = new Map(service.timeSeries.map((bucket) => [bucket.startTime, bucket]));
    return timeline.map((time, index) => observedChartRate(buckets.get(time), index, timeSeries.length, timeSeriesResolution));
  });
  const totals = new Map(timeSeries.map((bucket) => [bucket.startTime, bucket]));
  // The expanded dialog is intentionally much wider than the compact card.
  // Its wider view box preserves the chart's aspect ratio without horizontal
  // letterboxing, while the compact card retains its existing dimensions.
  const width = expanded ? 2000 : 960;
  const height = 540;
  const padding = { top: 32, right: 24, bottom: 82, left: 100 };
  const plotWidth = width - padding.left - padding.right;
  const finalTimelineIndex = timeline.length - 1;
  const minimumRange = Math.min(2, finalTimelineIndex);
  const rangeEnd = Math.max(minimumRange, Math.min(viewport.end ?? finalTimelineIndex, finalTimelineIndex));
  const rangeStart = Math.min(Math.max(0, viewport.start), Math.max(0, rangeEnd - minimumRange));
  const displayTimeline = timeline.slice(rangeStart, rangeEnd + 1);
  const displayValues = values.map((series) => series.slice(rangeStart, rangeEnd + 1));
  const displayTotals = displayTimeline.map((time, index) => observedChartRate(totals.get(time), rangeStart + index, timeSeries.length, timeSeriesResolution));
  const pinnedServiceKey = pinnedSeriesKey != null && services.some((service) => serviceKey(service) === pinnedSeriesKey)
    ? pinnedSeriesKey
    : null;
  const displayedRates = (pinnedServiceKey == null
    ? displayValues
    : displayValues.filter((_, index) => serviceKey(services[index]!) === pinnedServiceKey))
    .flat()
    .filter((rate): rate is number => rate != null);
  const maxRate = Math.max(...displayedRates, 1);
  const xFor = (index: number) => padding.left + index / Math.max(1, displayTimeline.length - 1) * plotWidth;
  const yFor = (value: number) => padding.top + (1 - value / maxRate) * (height - padding.top - padding.bottom);
  const sliderXFor = (index: number) => padding.left + index / Math.max(1, finalTimelineIndex) * plotWidth;
  const lines = values.map((_, index) => ({
    color: conversationLaneColors[index % conversationLaneColors.length],
    key: serviceKey(services[index]!),
    path: chartLinePath(displayValues[index]!, xFor, yFor)
  }));
  const visibleLines = pinnedServiceKey == null ? lines : lines.filter((line) => line.key === pinnedServiceKey);
  const activeServiceKey = pinnedServiceKey ?? highlightedServiceKey;
  const axisTicks = [1, 0.75, 0.5, 0.25, 0];
  const hoveredServices = hoveredIndex == null ? [] : services.map((service, index) => ({
      rank: index + 1,
      bucket: service.timeSeries.find((bucket) => bucket.startTime === displayTimeline[hoveredIndex]),
      service,
      rate: displayValues[index]?.[hoveredIndex] ?? null,
      color: conversationLaneColors[index % conversationLaneColors.length]
    })).filter(({ service }) => pinnedServiceKey == null || serviceKey(service) === pinnedServiceKey);
  const hovered = hoveredIndex == null ? null : {
    index: hoveredIndex,
    time: displayTimeline[hoveredIndex]!,
    total: pinnedServiceKey == null ? displayTotals[hoveredIndex] ?? null : hoveredServices[0]?.rate ?? null,
    services: hoveredServices
  };
  const hoverPosition = hovered == null ? 0 : xFor(hovered.index) / width * 100;
  const tooltipAlignment = hoverPosition > 68 ? ' flow-analytics__conversation-tooltip--end' : hoverPosition < 32 ? ' flow-analytics__conversation-tooltip--start' : '';
  const rangeControlTop = height - 58;
  const rangeControlBottom = height - 34;
  const minimumServiceChartWidth = 34;
  const maximumServiceChartWidth = 58;
  const chartPointAt = (svg: SVGSVGElement, clientX: number, clientY: number): DOMPoint | null => {
    const matrix = svg.getScreenCTM();
    if (!matrix) return null;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    return point.matrixTransform(matrix.inverse());
  };
  const sliderIndexAt = (chartX: number): number => Math.min(finalTimelineIndex, Math.max(0, Math.round((chartX - padding.left) / plotWidth * finalTimelineIndex)));
  const selectBucketAt = (chartX: number, pointerY: number): void => {
    const index = Math.round((chartX - padding.left) / plotWidth * (displayTimeline.length - 1));
    setHoveredIndex(Math.min(displayTimeline.length - 1, Math.max(0, index)));
    setHoveredPointerY(Math.min(100, Math.max(0, pointerY)));
  };
  const updateViewportFromDrag = (nextIndex: number): void => {
    const drag = rangeDragRef.current;
    if (!drag) return;
    const rangeWidth = drag.end - drag.start;
    let nextStart = drag.start;
    let nextEnd = drag.end;
    if (drag.type === 'start') nextStart = Math.min(nextIndex, drag.end - minimumRange);
    if (drag.type === 'end') nextEnd = Math.max(nextIndex, drag.start + minimumRange);
    if (drag.type === 'window') {
      nextStart = Math.min(Math.max(0, drag.start + nextIndex - drag.pointerIndex), finalTimelineIndex - rangeWidth);
      nextEnd = nextStart + rangeWidth;
    }
    setViewport({ start: nextStart, end: nextEnd });
    setHoveredIndex(null);
    setHoveredPointerY(null);
  };
  const updateServiceSplit = (clientX: number): void => {
    const bounds = serviceWorkspaceRef.current?.getBoundingClientRect();
    if (!bounds || bounds.width <= 0) return;
    const nextWidth = (clientX - bounds.left) / bounds.width * 100;
    setServiceChartWidth(Math.round(Math.min(maximumServiceChartWidth, Math.max(minimumServiceChartWidth, nextWidth)) * 10) / 10);
  };
  const serviceSplitStyle = { '--flow-conversation-chart-width': `${serviceChartWidth}%` } as CSSProperties;
  const serviceTitleId = expanded ? 'flow-service-pulse-title-expanded' : 'flow-service-pulse-title';
  const serviceExportContent: ChartExportContent = {
    title: 'Top 10 services',
    subtitle: 'Protocol and port groups · observed traffic rate',
    columns: ['Service', 'To service bytes', 'From service bytes', 'To service pkts', 'From service pkts', 'Share'],
    rows: services.map((service, index) => ({
      color: conversationLaneColors[index % conversationLaneColors.length],
      cells: [
        `${service.ianaServiceName ?? 'Unknown'} · ${service.label}`,
        formatBytes(service.toServiceBytes),
        formatBytes(service.fromServiceBytes),
        formatNumber(service.toServicePackets),
        formatNumber(service.fromServicePackets),
        formatTrafficShare(service.trafficSharePercent)
      ]
    })),
    timeAxis: {
      labels: chartExportTimeAxisLabels(displayTimeline),
      plotLeft: padding.left,
      plotRight: padding.right,
      labelY: height - 28
    }
  };

  return (
    <>
    <section ref={cardRef} className={`flow-analytics__conversation-pulse flow-analytics__service-pulse${expanded ? ' flow-analytics__conversation-pulse--expanded' : ''}`} aria-labelledby={serviceTitleId} style={serviceSplitStyle} onPointerDown={clearOnCardBackgroundPointerDown}>
      <div className="flow-analytics__card-heading flow-analytics__conversation-heading">
        <div className="flow-analytics__conversation-chart-heading"><div><h3 id={serviceTitleId}>Top 10 services</h3><span className="flow-analytics__chart-subtitle">Protocol and port groups · observed traffic rate</span></div><div className="flow-analytics__chart-actions">{expanded ? <ChartDownloadButton svgRef={serviceSvgRef} filename="flow-top-services.png" label="Top services chart" content={serviceExportContent} /> : null}<button type="button" className="flow-analytics__card-expand" onClick={() => expanded ? onClose?.() : setIsExpanded(true)} aria-label={expanded ? 'Close expanded services' : 'Expand Top 10 services'}>{expanded ? 'Close' : <><span aria-hidden="true">⤢</span><span className="sr-only">Expand Top 10 services</span></>}</button></div></div>
        <span className="flow-analytics__conversation-split-gutter" aria-hidden="true" />
        <div className="flow-analytics__conversation-table-heading"><span className="flow-analytics__eyebrow">Service traffic totals</span><strong>Top 10 services</strong></div>
      </div>
      <div className="flow-analytics__conversation-workspace" ref={serviceWorkspaceRef}>
        <figure>
          <svg ref={serviceSvgRef} viewBox={`0 0 ${width} ${height}`} role="img" tabIndex={0}
            aria-label="Traffic-rate line chart with one line per top ten port and protocol service group. Hover a service row or chart line to connect the two views. Drag either lower handle to resize the time range, or drag the highlighted range to slide it through time. Hover and use the left and right arrow keys to inspect a minute."
            onFocus={() => setHoveredIndex((index) => index ?? 0)}
            onBlur={() => { setHoveredIndex(null); setHoveredPointerY(null); }}
            onPointerEnter={(event) => { const chartPoint = chartPointAt(event.currentTarget, event.clientX, event.clientY); if (!chartPoint) return; const bounds = event.currentTarget.getBoundingClientRect(); selectBucketAt(chartPoint.x, (event.clientY - bounds.top) / bounds.height * 100); }}
            onPointerDown={(event) => { const chartPoint = chartPointAt(event.currentTarget, event.clientX, event.clientY); if (!chartPoint || chartPoint.y < rangeControlTop - 8 || chartPoint.y > rangeControlBottom + 8) return; const nextIndex = sliderIndexAt(chartPoint.x); const handleHitRadius = 22; const distanceToStart = Math.abs(sliderXFor(nextIndex) - sliderXFor(rangeStart)); const distanceToEnd = Math.abs(sliderXFor(nextIndex) - sliderXFor(rangeEnd)); const type = distanceToStart <= handleHitRadius ? 'start' : distanceToEnd <= handleHitRadius ? 'end' : nextIndex >= rangeStart && nextIndex <= rangeEnd ? 'window' : null; if (!type) return; /* Preserve a selected service while its chart range is adjusted. */ event.stopPropagation(); event.currentTarget.setPointerCapture(event.pointerId); rangeDragRef.current = { type, pointerIndex: nextIndex, start: rangeStart, end: rangeEnd }; setRangeInteraction(type); setHoveredIndex(null); setHoveredPointerY(null); }}
            onPointerMove={(event) => { const chartPoint = chartPointAt(event.currentTarget, event.clientX, event.clientY); if (!chartPoint) return; if (rangeDragRef.current) { updateViewportFromDrag(sliderIndexAt(chartPoint.x)); return; } const bounds = event.currentTarget.getBoundingClientRect(); selectBucketAt(chartPoint.x, (event.clientY - bounds.top) / bounds.height * 100); }}
            onPointerUp={(event) => { if (!rangeDragRef.current) return; if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); rangeDragRef.current = null; setRangeInteraction(null); }}
            onPointerCancel={() => { rangeDragRef.current = null; setRangeInteraction(null); setHoveredPointerY(null); }}
            onPointerLeave={() => { if (!rangeDragRef.current) { setHoveredIndex(null); setHoveredPointerY(null); setHighlightedServiceKey(null); } }}
            onKeyDown={(event) => { if (event.key === 'ArrowLeft') { event.preventDefault(); setHoveredPointerY(null); setHoveredIndex((index) => Math.max(0, (index ?? 0) - 1)); } if (event.key === 'ArrowRight') { event.preventDefault(); setHoveredPointerY(null); setHoveredIndex((index) => Math.min(displayTimeline.length - 1, (index ?? 0) + 1)); } }}>
            {axisTicks.map((tick) => { const y = yFor(maxRate * tick); return <g key={tick}><line x1={padding.left} x2={width - padding.right} y1={y} y2={y} className="flow-analytics__conversation-grid" /><text x={padding.left - 12} y={y + 4} textAnchor="end">{formatObservedRate(maxRate * tick)}</text></g>; })}
            {visibleLines.map((line) => <path key={`${line.key}-hit-target`} d={line.path} className="flow-analytics__conversation-series-hit-target" onPointerEnter={() => setHighlightedServiceKey(line.key)} onPointerLeave={() => setHighlightedServiceKey(null)} />)}
            {visibleLines.map((line) => <path key={line.key} d={line.path} stroke={line.color} pointerEvents="none" className={`flow-analytics__conversation-series-line${activeServiceKey === line.key ? ' flow-analytics__conversation-series-line--highlighted' : activeServiceKey ? ' flow-analytics__conversation-series-line--muted' : ''}`} />)}
            {hovered ? <g className="flow-analytics__conversation-hover" aria-hidden="true" pointerEvents="none"><line x1={xFor(hovered.index)} x2={xFor(hovered.index)} y1={padding.top} y2={height - padding.bottom} />{hovered.services.map(({ rate, color, service }) => rate == null ? null : <circle key={serviceKey(service)} cx={xFor(hovered.index)} cy={yFor(rate)} r="4" stroke={color} />)}</g> : null}
            <g className={'flow-analytics__conversation-range-control' + (rangeInteraction ? ' flow-analytics__conversation-range-control--dragging' : '')} aria-hidden="true"><rect x={padding.left} y={height - 53} width={plotWidth} height="14" rx="3" /><rect x={sliderXFor(rangeStart) + 3} y={height - 50} width={Math.max(0, sliderXFor(rangeEnd) - sliderXFor(rangeStart) - 6)} height="8" rx="2" /><rect className="flow-analytics__conversation-range-window" x={sliderXFor(rangeStart) + 8} y={height - 56} width={Math.max(0, sliderXFor(rangeEnd) - sliderXFor(rangeStart) - 16)} height="20" rx="3" /><rect className="flow-analytics__conversation-range-handle" x={sliderXFor(rangeStart) - 11} y={height - 61} width="22" height="30" rx="3" /><rect className="flow-analytics__conversation-range-handle" x={sliderXFor(rangeEnd) - 11} y={height - 61} width="22" height="30" rx="3" /><path d={`M ${sliderXFor(rangeStart)} ${height - 58} v 24 M ${sliderXFor(rangeEnd)} ${height - 58} v 24`} /></g>
            <text className="flow-analytics__chart-range-time-label" x={padding.left} y={height - 14}>{formatChartTime(displayTimeline[0]!)}</text><text className="flow-analytics__chart-range-time-label" x={width - padding.right} y={height - 14} textAnchor="end">{formatChartTime(displayTimeline.at(-1)!)}</text>
          </svg>
          {hovered ? <aside className={'flow-analytics__conversation-tooltip' + tooltipAlignment} style={hoveredPointerY == null ? { left: `${hoverPosition}%` } : hoveredPointerY < 50 ? { left: `${hoverPosition}%`, top: `calc(${hoveredPointerY}% + 1.25rem)` } : { left: `${hoverPosition}%`, top: 'auto', bottom: `calc(${100 - hoveredPointerY}% + 1.25rem)` }} role="status"><header><strong>{formatDateTime(hovered.time)}</strong><span>{formatObservedRate(hovered.total)} {pinnedServiceKey == null ? 'total' : 'selected'}</span></header><ol>{hovered.services.map(({ service, rate, bucket, color, rank }) => <li key={serviceKey(service)}><i style={{ backgroundColor: color }} /><div><strong>#{rank} {service.ianaServiceName ?? 'Unknown'}</strong><span>{service.label} · normalized service group</span></div><em>{formatObservedRate(rate)}<small>{formatBytes(bucket?.recordCount ? bucket.bytes : null)} · {bucket?.recordCount ? formatNumber(bucket.packets) : '—'} pkt</small></em></li>)}</ol></aside> : null}
        </figure>
        <div className={`flow-analytics__conversation-splitter${isServiceSplitDragging ? ' flow-analytics__conversation-splitter--dragging' : ''}`} role="separator" aria-label="Resize chart and service table" aria-orientation="vertical" aria-valuemin={minimumServiceChartWidth} aria-valuemax={maximumServiceChartWidth} aria-valuenow={Math.round(serviceChartWidth)} aria-valuetext={`${Math.round(serviceChartWidth)}% chart, ${Math.round(100 - serviceChartWidth)}% table`} tabIndex={0}
          onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); setIsServiceSplitDragging(true); updateServiceSplit(event.clientX); }} onPointerMove={(event) => { if (event.currentTarget.hasPointerCapture(event.pointerId)) updateServiceSplit(event.clientX); }} onPointerUp={(event) => { if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); setIsServiceSplitDragging(false); }} onPointerCancel={() => setIsServiceSplitDragging(false)} onKeyDown={(event) => { if (event.key === 'ArrowLeft') { event.preventDefault(); setServiceChartWidth((width) => Math.max(minimumServiceChartWidth, width - 2)); } if (event.key === 'ArrowRight') { event.preventDefault(); setServiceChartWidth((width) => Math.min(maximumServiceChartWidth, width + 2)); } if (event.key === 'Home') { event.preventDefault(); setServiceChartWidth(minimumServiceChartWidth); } if (event.key === 'End') { event.preventDefault(); setServiceChartWidth(maximumServiceChartWidth); } }}><span aria-hidden="true" /></div>
        <aside className="flow-analytics__conversation-list" aria-label="Top ten services">
          <div className="flow-analytics__conversation-table-scroll">
            <table>
              <colgroup>
                <col className="flow-analytics__conversation-column" />
                <col className="flow-analytics__sent-bytes-column" />
                <col className="flow-analytics__received-bytes-column" />
                <col className="flow-analytics__sent-packets-column" />
                <col className="flow-analytics__received-packets-column" />
                <col className="flow-analytics__traffic-share-column" />
              </colgroup>
              <thead>
                <tr><th scope="col">Service</th><th scope="col">To service bytes</th><th scope="col">From service bytes</th><th scope="col">To service pkts</th><th scope="col">From service pkts</th><th scope="col">Share</th></tr>
              </thead>
              <tbody>
                {services.map((service, index) => {
                  const key = serviceKey(service);
                  return <tr key={key} data-flow-analytics-series-row className={`flow-analytics__conversation-row${activeServiceKey === key ? ' flow-analytics__conversation-row--highlighted' : ''}`} onClick={() => setPinnedSeriesKey(key)} onPointerEnter={() => setHighlightedServiceKey(key)} onPointerLeave={() => setHighlightedServiceKey((current) => current === key ? null : current)}>
                    <td><span className="flow-analytics__conversation-toggle"><i style={{ backgroundColor: conversationLaneColors[index % conversationLaneColors.length] }} /><span className="flow-analytics__conversation-rank">{index + 1}</span><span aria-hidden="true" /><span className="flow-analytics__service-identity"><strong>{service.ianaServiceName ?? 'Unknown'}</strong><small>{service.label}</small></span></span></td>
                    <td>{formatBytes(service.toServiceBytes)}</td>
                    <td>{formatBytes(service.fromServiceBytes)}</td>
                    <td>{formatNumber(service.toServicePackets)}</td>
                    <td>{formatNumber(service.fromServicePackets)}</td>
                    <td>{formatTrafficShare(service.trafficSharePercent)}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </aside>
      </div>
      <p className="flow-analytics__card-disclosure">Each row is a server-derived normalized protocol and port group. The service selects the port using port-range and numeric-order rules, then, when available, shows the exact IANA registry name for that selected protocol and port. The name is not DPI or authoritative application identification. To service and from service are measured relative to the selected port. Click a row to show only its chart line; click elsewhere to restore every line. Drag the lower handles to zoom the visible time window.</p>
    </section>
    {!expanded && isExpanded ? <FlowAnalyticsExpandedModal label="Top 10 services" onClose={() => setIsExpanded(false)}><ServicePulse items={items} timeSeries={timeSeries} timeSeriesResolution={timeSeriesResolution} expanded onClose={() => setIsExpanded(false)} /></FlowAnalyticsExpandedModal> : null}
    </>
  );
};

const qualityTone = (metadata: FlowQualityMetadata): 'success' | 'warning' | 'ghost' => {
  if (metadata.samplingState === 'sampled' || metadata.samplingState === 'mixed') return 'warning';
  if (metadata.samplingState === 'unsampled') return 'success';
  return 'ghost';
};

const QualitySummary = ({ metadata }: { metadata: FlowQualityMetadata }) => (
  <aside className={'flow-analytics__quality flow-analytics__quality--' + qualityTone(metadata)}>
    <div>
      <strong>Flow query</strong>
      <p>Server results use the selected event-time range. Sampling state is reported, but completeness is not inferred.</p>
    </div>
    <dl>
      <div><dt>Sampling</dt><dd>{metadata.samplingState}</dd></div>
      <div><dt>Effective range</dt><dd>{formatDateTime(metadata.effectiveStartTime) + ' – ' + formatDateTime(metadata.effectiveEndTime)}</dd></div>
    </dl>
  </aside>
);

const QueryState = ({ loading, error, backendMissing, emptyMessage }: {
  loading: boolean;
  error: Error | null;
  backendMissing: boolean;
  emptyMessage: string;
}) => {
  if (loading) return <p className="flow-analytics__message">Loading flow data…</p>;
  if (error) return <p className="flow-analytics__message flow-analytics__message--error">{backendMissing ? 'This flow API is unavailable in the selected environment.' : 'Unable to load flow data: ' + error.message}</p>;
  return <p className="flow-analytics__message">{emptyMessage}</p>;
};

const PageNavigation = ({ meta, onPrevious, onNext }: {
  meta: PageMeta;
  onPrevious: () => void;
  onNext: () => void;
}) => {
  if (!meta.prev && !meta.next) return null;
  return (
    <div className="flow-analytics__pagination">
      {meta.prev ? <button type="button" className="ui-button ui-button--ghost" onClick={onPrevious}>Previous page</button> : <span />}
      {meta.next ? <button type="button" className="ui-button ui-button--ghost" onClick={onNext}>Next page</button> : null}
    </div>
  );
};

const afterParameter = (cursor: string | null): string => cursor ? '&after=' + encodeURIComponent(cursor) : '';

// The shared query hook retains its previous response while refreshing. Tag
// flow responses with their scope so an organization result cannot appear in
// the site view (or vice versa) during a scope change.
const useScopedFlowQuery = <T,>(fetcher: (() => Promise<T>) | null, scopeKey: string) => {
  const taggedFetcher = useMemo(() => fetcher ? async () => ({ scopeKey, payload: await fetcher() }) : null, [fetcher, scopeKey]);
  const query = useApiQuery(taggedFetcher, [taggedFetcher]);
  return {
    ...query,
    data: query.data?.scopeKey === scopeKey ? query.data.payload : null
  };
};

const FlowAnalyticsPage = () => {
  const orgId = useActiveOrgId();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchWithAuthRetry, fetchOrgCollection } = useAuthFetch();
  const initialWindow = useMemo(makeDefaultWindow, []);
  const [draftStart, setDraftStart] = useState(() => toLocalInput(initialWindow.startTime));
  const [draftEnd, setDraftEnd] = useState(() => toLocalInput(initialWindow.endTime));
  const [window, setWindow] = useState<FlowWindow>(initialWindow);
  const [windowError, setWindowError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [scopeMode, setScopeMode] = useState<'org' | 'site'>('org');
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [sitePickerOpen, setSitePickerOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [interfaceCursor, setInterfaceCursor] = useState<string | null>(null);
  const [expandedFlowSource, setExpandedFlowSource] = useState<FlowSource | null>(null);
  const [trendSelection, setTrendSelection] = useState<FlowWindow | null>(null);
  const [reportState, setReportState] = useState<'idle' | 'preparing' | 'downloading'>('idle');
  const [reportError, setReportError] = useState<string | null>(null);
  const [sourceThroughputWidth, setSourceThroughputWidth] = useState(50);
  const [isSourceThroughputSplitDragging, setIsSourceThroughputSplitDragging] = useState(false);
  const sourceThroughputWorkspaceRef = useRef<HTMLDivElement>(null);
  const sitePickerRef = useRef<HTMLDivElement>(null);
  const siteTriggerRef = useRef<HTMLButtonElement>(null);

  const minimumSourceThroughputWidth = 42;
  const maximumSourceThroughputWidth = 72;
  const updateSourceThroughputSplit = useCallback((clientX: number): void => {
    const bounds = sourceThroughputWorkspaceRef.current?.getBoundingClientRect();
    if (!bounds || bounds.width <= 0) return;
    const nextWidth = (clientX - bounds.left) / bounds.width * 100;
    setSourceThroughputWidth(Math.round(Math.min(maximumSourceThroughputWidth, Math.max(minimumSourceThroughputWidth, nextWidth)) * 10) / 10);
  }, []);
  const sourceThroughputSplitStyle = { '--flow-source-throughput-width': `${sourceThroughputWidth}%` } as CSSProperties;
  const selectTrendRange = useCallback((next: FlowWindow): void => {
    const normalized = normalizeFlowWindow(next);
    if (!normalized) return;
    setTrendSelection(normalized);
  }, []);
  const clearTrendSelection = useCallback((): void => setTrendSelection(null), []);

  useEffect(() => {
    setScopeMode('org');
    setSelectedSiteId(null);
    setSitePickerOpen(false);
    setSiteSearch('');
  }, [orgId]);

  useEffect(() => {
    if (!sitePickerOpen) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!sitePickerRef.current?.contains(event.target as Node)) setSitePickerOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [sitePickerOpen]);

  const sitesFetcher = useMemo(() => {
    if (!orgId || (!sitePickerOpen && scopeMode !== 'site')) return null;
    return async (): Promise<SiteSummary[]> => {
      const sites: SiteSummary[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < 25; page += 1) {
        const parameters = new URLSearchParams({ limit: '200' });
        if (cursor) parameters.set('after', cursor);
        const response = await fetchOrgCollection<SiteSummary>(basePath, orgId, 'sites?' + parameters.toString(), { cache: 'no-store' });
        sites.push(...(response.items ?? []));
        cursor = response.meta?.next ?? null;
        if (!cursor) return sites;
      }
      throw new Error('Site list is too large to display. Narrow the site list before selecting a scope.');
    };
  }, [basePath, fetchOrgCollection, orgId, refreshNonce, scopeMode, sitePickerOpen]);
  const sitesQuery = useScopedFlowQuery<SiteSummary[]>(sitesFetcher, orgId ?? '');
  const siteOptions = useMemo(() => (sitesQuery.data ?? [])
    .filter((site): site is SiteSummary & { id: string } => Boolean(site.id))
    .map((site) => ({ ...site, id: site.id.toLowerCase() }))
    .sort((left, right) => (left.name ?? '').localeCompare(right.name ?? '')), [sitesQuery.data]);
  const siteNameById = useMemo(() => new Map(siteOptions.map((site) => [site.id.toLowerCase(), site.name?.trim() || site.id])), [siteOptions]);
  const visibleSiteOptions = useMemo(() => siteOptions.filter((site) =>
    (site.name ?? site.id).toLowerCase().includes(siteSearch.trim().toLowerCase())), [siteOptions, siteSearch]);
  const selectedSiteName = selectedSiteId ? siteNameById.get(selectedSiteId) ?? selectedSiteId : null;
  const canQueryFlows = Boolean(orgId && (scopeMode === 'org' || selectedSiteId));
  const scopeKey = (orgId ?? '') + ':' + scopeMode + ':' + (selectedSiteId ?? '');
  const resetScopeDetails = useCallback(() => {
    setInterfaceCursor(null);
    setExpandedFlowSource(null);
    setTrendSelection(null);
  }, []);
  const openSitePicker = useCallback(() => {
    if (sitePickerOpen) {
      setSitePickerOpen(false);
      return;
    }
    setSiteSearch('');
    setSitePickerOpen(true);
  }, [sitePickerOpen]);
  const selectSite = useCallback((siteId: string) => {
    setSelectedSiteId(siteId);
    setScopeMode('site');
    setSitePickerOpen(false);
    resetScopeDetails();
    siteTriggerRef.current?.focus();
  }, [resetScopeDetails]);

  const query = useMemo(() => {
    const parameters = new URLSearchParams({ startTime: window.startTime, endTime: window.endTime });
    if (scopeMode === 'site' && selectedSiteId) parameters.set('siteId', selectedSiteId);
    return parameters.toString();
  }, [scopeMode, selectedSiteId, window]);
  const fetchFlow = useCallback(async <T,>(resource: string): Promise<T> => {
    if (!orgId) throw new Error('An organization is required.');
    const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, resource), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new ApiResponseError(response, 'Request failed with status ' + response.status);
    return (await response.json()) as T;
  }, [basePath, fetchWithAuthRetry, orgId]);

  const createFlowReport = useCallback(async (request: ApiFlowReportCreateRequest): Promise<FlowReport> => {
    if (!orgId) throw new Error('An organization is required.');
    const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, 'flows/reports'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify({
        ...request,
        startTime: request.startTime.toISOString(),
        endTime: request.endTime.toISOString(),
        visibleStartTime: request.visibleStartTime.toISOString(),
        visibleEndTime: request.visibleEndTime.toISOString()
      })
    });
    if (!response.ok) throw new ApiResponseError(response, 'Report request failed with status ' + response.status);
    return (await response.json()) as FlowReport;
  }, [basePath, fetchWithAuthRetry, orgId]);

  const downloadReport = useCallback(async (): Promise<void> => {
    if (!canQueryFlows || !orgId || reportState !== 'idle') return;
    const visibleWindow = trendSelection ?? window;
    const reportRequest: ApiFlowReportCreateRequest = {
      format: 'pdf',
      startTime: new Date(window.startTime),
      endTime: new Date(window.endTime),
      visibleStartTime: new Date(visibleWindow.startTime),
      visibleEndTime: new Date(visibleWindow.endTime),
      ...(scopeMode === 'site' && selectedSiteId ? { siteId: selectedSiteId } : {})
    };
    setReportError(null);
    setReportState('preparing');
    try {
      let report = await createFlowReport(reportRequest);
      const deadline = Date.now() + 35_000;
      while (report.status === 'queued' || report.status === 'running') {
        if (Date.now() >= deadline) throw new Error('The report is still preparing. Please try again shortly.');
        await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 750));
        report = await fetchFlow<FlowReport>('flows/reports/' + encodeURIComponent(report.id));
      }
      if (report.status !== 'completed') {
        throw new Error(report.error?.message || 'The report could not be generated.');
      }
      setReportState('downloading');
      const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, 'flows/reports/' + encodeURIComponent(report.id) + '/download'), {
        method: 'GET',
        headers: { Accept: 'application/pdf' },
        cache: 'no-store'
      });
      if (!response.ok) throw new ApiResponseError(response, 'Report download failed with status ' + response.status);
      downloadBlobAsFile(await response.blob(), report.fileName || 'flow-analytics-report.pdf');
    } catch (reason) {
      setReportError(reason instanceof Error ? reason.message : 'The report could not be generated.');
    } finally {
      setReportState('idle');
    }
  }, [basePath, canQueryFlows, createFlowReport, fetchFlow, fetchWithAuthRetry, orgId, reportState, scopeMode, selectedSiteId, trendSelection, window]);

  const overviewFetcher = useMemo(() => canQueryFlows ? () => fetchFlow<FlowOverview>('flows/overview?' + query + '&topLimit=10') : null, [canQueryFlows, fetchFlow, query, refreshNonce]);
  const trendSelectionOverviewFetcher = useMemo(() => {
    if (!canQueryFlows || !trendSelection) return null;
    const parameters = new URLSearchParams({ startTime: trendSelection.startTime, endTime: trendSelection.endTime, topLimit: '1' });
    if (scopeMode === 'site' && selectedSiteId) parameters.set('siteId', selectedSiteId);
    return () => fetchFlow<FlowOverview>('flows/overview?' + parameters.toString());
  }, [canQueryFlows, fetchFlow, refreshNonce, scopeMode, selectedSiteId, trendSelection]);
  const overviewConversationsFetcher = useMemo(() => canQueryFlows ? () => fetchFlow<FlowConversations>('flows/conversations?' + query + '&limit=10&mode=endpoint_pair') : null, [canQueryFlows, fetchFlow, query, refreshNonce]);
  const endpointsFetcher = useMemo(() => canQueryFlows ? () => fetchFlow<FlowEndpoints>('flows/endpoints?' + query + '&limit=10') : null, [canQueryFlows, fetchFlow, query, refreshNonce]);
  const servicesFetcher = useMemo(() => canQueryFlows ? () => fetchFlow<FlowServices>('flows/services?' + query + '&limit=10') : null, [canQueryFlows, fetchFlow, query, refreshNonce]);
  const interfacesFetcher = useMemo(() => canQueryFlows ? () => fetchFlow<FlowInterfaceTotalsResponse>('flows/interface-totals?' + query + '&limit=10' + afterParameter(interfaceCursor)) : null, [canQueryFlows, fetchFlow, interfaceCursor, query, refreshNonce]);
  const sourceInterfaceContextFetcher = useMemo(() => {
    if (!canQueryFlows || !expandedFlowSource) return null;
    const parameters = new URLSearchParams({
      startTime: window.startTime,
      endTime: window.endTime,
      limit: '50',
      flowType: expandedFlowSource.flowType,
      samplerAddress: expandedFlowSource.samplerAddress,
      observationDomainId: String(expandedFlowSource.observationDomainId)
    });
    if (scopeMode === 'site' && selectedSiteId) parameters.set('siteId', selectedSiteId);
    return () => fetchFlow<FlowInterfaceTotalsResponse>('flows/interface-totals?' + parameters.toString());
  }, [canQueryFlows, expandedFlowSource, fetchFlow, refreshNonce, scopeMode, selectedSiteId, window]);
  const sourcesFetcher = useMemo(() => canQueryFlows ? () => fetchFlow<FlowSources>('flow-sources?' + query + '&limit=50') : null, [canQueryFlows, fetchFlow, query, refreshNonce]);

  const currentScopeKey = scopeKey + ':' + query;
  const overviewQuery = useScopedFlowQuery<FlowOverview>(overviewFetcher, currentScopeKey);
  const trendSelectionOverviewQuery = useScopedFlowQuery<FlowOverview>(trendSelectionOverviewFetcher, currentScopeKey + ':' + (trendSelection?.startTime ?? '') + ':' + (trendSelection?.endTime ?? ''));
  const overviewConversationsQuery = useScopedFlowQuery<FlowConversations>(overviewConversationsFetcher, currentScopeKey);
  const endpointsQuery = useScopedFlowQuery<FlowEndpoints>(endpointsFetcher, currentScopeKey);
  const servicesQuery = useScopedFlowQuery<FlowServices>(servicesFetcher, currentScopeKey);
  const interfacesQuery = useScopedFlowQuery<FlowInterfaceTotalsResponse>(interfacesFetcher, currentScopeKey + ':' + (interfaceCursor ?? ''));
  const sourceInterfaceContextQuery = useScopedFlowQuery<FlowInterfaceTotalsResponse>(sourceInterfaceContextFetcher, currentScopeKey + ':' + (expandedFlowSource?.samplerAddress ?? ''));
  const sourcesQuery = useScopedFlowQuery<FlowSources>(sourcesFetcher, currentScopeKey);

  const applyWindow = useCallback(() => {
    const startTime = toISO(draftStart);
    const endTime = toISO(draftEnd);
    if (!startTime || !endTime || new Date(endTime).getTime() <= new Date(startTime).getTime()) {
      setWindowError('Choose a valid start and end time, with end after start.');
      return;
    }
    if (new Date(endTime).getTime() - new Date(startTime).getTime() > 24 * 60 * 60 * 1000) {
      setWindowError('The current raw-flow API supports intervals of up to 24 hours.');
      return;
    }
    setWindowError(null);
    setInterfaceCursor(null);
    setExpandedFlowSource(null);
    setTrendSelection(null);
    setWindow({ startTime, endTime });
  }, [draftEnd, draftStart]);

  const resetToLastHour = useCallback(() => {
    const next = makeDefaultWindow(1);
    setDraftStart(toLocalInput(next.startTime));
    setDraftEnd(toLocalInput(next.endTime));
    setWindowError(null);
    setInterfaceCursor(null);
    setExpandedFlowSource(null);
    setTrendSelection(null);
    setWindow(next);
  }, []);

  const setPresetWindow = useCallback((hours: number) => {
    const next = makeDefaultWindow(hours);
    setDraftStart(toLocalInput(next.startTime));
    setDraftEnd(toLocalInput(next.endTime));
    setWindowError(null);
    setInterfaceCursor(null);
    setExpandedFlowSource(null);
    setTrendSelection(null);
    setWindow(next);
  }, []);

  if (!orgId) {
    return <OrgModuleLayout title="Flow Analytics" description="Observed network traffic from stored exporter records." scrollMode="all" topSlot={<OrgModulePlaceholder intent="Select an organization to query its stored flow data." />} />;
  }

  const metadata = overviewQuery.data?.metadata ?? interfacesQuery.data?.metadata ?? sourcesQuery.data?.metadata;
  const siteBreakdown = overviewQuery.data?.siteBreakdown;
  const siteBreakdownReady = scopeMode === 'org' || (Array.isArray(siteBreakdown) &&
    Boolean(selectedSiteId) && siteBreakdown.some((item) => item.siteId.toLowerCase() === selectedSiteId));
  const topNote = scopeMode === 'org'
    ? 'Top values are aggregated by the service over the selected event-time interval.'
    : `Top values reflect ${selectedSiteName ?? 'the selected site'} over the selected interval.`;
  const trendScopeDescription = scopeMode === 'org'
    ? 'Organization-wide observed traffic across all matching flow sources.'
    : `Observed traffic for ${selectedSiteName ?? 'the selected site'} across matching flow sources.`;
  const scopeLabel = scopeMode === 'org' ? 'Entire organization' : selectedSiteName ?? 'Selected site';
  return (
    <OrgModuleLayout
      title="Flow Analytics"
      description="Observed IPFIX, NetFlow, and sFlow records."
      scrollMode="all"
      actions={<div className="flow-analytics__page-actions">
        <button type="button" className="ui-button ui-button--ghost" onClick={() => { void downloadReport(); }} disabled={!canQueryFlows || reportState !== 'idle'}>
          <DownloadIcon size={16} /> {reportState === 'preparing' ? 'Preparing report…' : reportState === 'downloading' ? 'Downloading report…' : 'Download report'}
        </button>
        <button type="button" className="ui-button ui-button--ghost" onClick={() => setRefreshNonce((value) => value + 1)}>Refresh</button>
      </div>}
      topSlot={
        <div className="flow-analytics__controls">
          <div className="flow-analytics__range">
            <div className="flow-analytics__scope-field">
              <span>Scope</span>
              <div className="flow-analytics__scope" role="group" aria-label="Flow analytics scope">
                <button type="button" className="ui-button" aria-pressed={scopeMode === 'org'} onClick={() => { setScopeMode('org'); setSitePickerOpen(false); resetScopeDetails(); }}>Organization</button>
                <div className="flow-analytics__site-picker" ref={sitePickerRef}>
                  <button ref={siteTriggerRef} type="button" className="ui-button" aria-pressed={scopeMode === 'site'} aria-expanded={sitePickerOpen} aria-controls={sitePickerOpen ? 'flow-site-picker' : undefined} aria-label={scopeMode === 'site' && selectedSiteName ? `Site: ${selectedSiteName}` : 'Site'} title={scopeMode === 'site' ? selectedSiteName ?? undefined : undefined} onClick={openSitePicker}>Site</button>
                  {sitePickerOpen ? <div id="flow-site-picker" className="flow-analytics__site-picker-body" role="dialog" aria-label="Select a site" onKeyDown={(event) => { if (event.key === 'Escape') setSitePickerOpen(false); }}>
                    <label className="flow-analytics__select">Find site<input autoFocus type="search" value={siteSearch} onChange={(event) => setSiteSearch(event.target.value)} placeholder="Search site names" /></label>
                    {sitesQuery.loading ? <p>Loading sites…</p> : null}
                    {sitesQuery.error ? <p className="flow-analytics__message flow-analytics__message--error">Unable to load site names: {sitesQuery.error.message}</p> : null}
                    {!sitesQuery.loading && !sitesQuery.error && visibleSiteOptions.length === 0 ? <p>No sites match this search.</p> : null}
                    <div className="flow-analytics__site-options">
                      {visibleSiteOptions.map((site) => (
                        <button key={site.id} type="button" aria-current={scopeMode === 'site' && selectedSiteId === site.id ? 'true' : undefined} onClick={() => selectSite(site.id)}>{site.name?.trim() || site.id}</button>
                      ))}
                    </div>
                  </div> : null}
                </div>
              </div>
            </div>
            <label>Start<input type="datetime-local" value={draftStart} onChange={(event) => setDraftStart(event.target.value)} /></label>
            <label>End<input type="datetime-local" value={draftEnd} onChange={(event) => setDraftEnd(event.target.value)} /></label>
            <button type="button" className="ui-button" onClick={applyWindow}>Apply range</button>
            <button type="button" className="ui-button ui-button--ghost" onClick={resetToLastHour}>Last hour</button>
            <button type="button" className="ui-button ui-button--ghost" onClick={() => setPresetWindow(3)}>Last 3 hours</button>
            <button type="button" className="ui-button ui-button--ghost" onClick={() => setPresetWindow(24)}>Last 24 hours</button>
          </div>
          <p className="flow-analytics__range-note">Queries use a half-open range: {formatDateTime(window.startTime)} to {formatDateTime(window.endTime)}.</p>
          {windowError ? <p className="flow-analytics__message flow-analytics__message--error">{windowError}</p> : null}
          {reportError ? <p className="flow-analytics__message flow-analytics__message--error" role="status">Unable to download report: {reportError}</p> : null}
          {metadata ? <QualitySummary metadata={metadata} /> : null}
        </div>
      }
    >
      <div className="flow-analytics">
        <section className="flow-analytics__section" aria-labelledby="flow-overview-title">
          <div className="flow-analytics__section-heading">
            <div><h2 id="flow-overview-title">Traffic explorer</h2><p>{topNote}</p></div>
            {overviewQuery.data ? <span className={'ui-badge ui-badge--' + qualityTone(overviewQuery.data.metadata)}>{overviewQuery.data.metadata.samplingState}</span> : null}
          </div>
          {!canQueryFlows ? <p className="flow-analytics__message">Select a site to load site traffic.</p> : overviewQuery.data && !siteBreakdownReady ? (
            <p className="flow-analytics__message flow-analytics__message--error">This flow API has not confirmed the selected site. Site traffic cannot be displayed safely.</p>
          ) : overviewQuery.data ? (
            <>
              <div className="flow-analytics__dashboard-grid">
                {overviewConversationsQuery.data ? <ConversationPulse items={overviewConversationsQuery.data.items} timeSeries={overviewConversationsQuery.data.timeSeries} timeSeriesResolution={overviewConversationsQuery.data.metadata.timeSeriesResolution} /> : null}
                {endpointsQuery.data ? <EndpointPulse items={endpointsQuery.data.items} timeSeries={endpointsQuery.data.timeSeries} timeSeriesResolution={endpointsQuery.data.metadata.timeSeriesResolution} /> : null}
                {servicesQuery.data ? <ServicePulse items={servicesQuery.data.items} timeSeries={servicesQuery.data.timeSeries} timeSeriesResolution={servicesQuery.data.metadata.timeSeriesResolution} /> : null}
                <div className="flow-analytics__source-throughput-workspace" ref={sourceThroughputWorkspaceRef} style={sourceThroughputSplitStyle}>
                  <FlowSourceSnapshot items={sourcesQuery.data?.items ?? []} expandedSource={expandedFlowSource} interfaceContext={sourceInterfaceContextQuery.data?.items ?? null} interfaceContextLoading={sourceInterfaceContextQuery.loading} interfaceContextError={sourceInterfaceContextQuery.error?.message ?? null} onExpandedSourceChange={setExpandedFlowSource} />
                  <div
                    className={`flow-analytics__conversation-splitter${isSourceThroughputSplitDragging ? ' flow-analytics__conversation-splitter--dragging' : ''}`}
                    role="separator"
                    aria-label="Resize Flow sources and observed throughput cards"
                    aria-orientation="vertical"
                    aria-valuemin={minimumSourceThroughputWidth}
                    aria-valuemax={maximumSourceThroughputWidth}
                    aria-valuenow={Math.round(sourceThroughputWidth)}
                    aria-valuetext={`${Math.round(sourceThroughputWidth)}% Flow sources, ${Math.round(100 - sourceThroughputWidth)}% observed throughput`}
                    tabIndex={0}
                    onPointerDown={(event) => {
                      event.currentTarget.setPointerCapture(event.pointerId);
                      setIsSourceThroughputSplitDragging(true);
                      updateSourceThroughputSplit(event.clientX);
                    }}
                    onPointerMove={(event) => {
                      if (event.currentTarget.hasPointerCapture(event.pointerId)) updateSourceThroughputSplit(event.clientX);
                    }}
                    onPointerUp={(event) => {
                      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
                      setIsSourceThroughputSplitDragging(false);
                    }}
                    onPointerCancel={() => setIsSourceThroughputSplitDragging(false)}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowLeft') { event.preventDefault(); setSourceThroughputWidth((width) => Math.max(minimumSourceThroughputWidth, width - 2)); }
                      if (event.key === 'ArrowRight') { event.preventDefault(); setSourceThroughputWidth((width) => Math.min(maximumSourceThroughputWidth, width + 2)); }
                      if (event.key === 'Home') { event.preventDefault(); setSourceThroughputWidth(minimumSourceThroughputWidth); }
                      if (event.key === 'End') { event.preventDefault(); setSourceThroughputWidth(maximumSourceThroughputWidth); }
                    }}
                  ><span aria-hidden="true" /></div>
                  <section className="flow-analytics__trend-card">
                    <TrafficTrend
                      series={overviewQuery.data.timeSeries}
                      observedBytesPerSecond={overviewQuery.data.observedBytesPerSecond}
                      observedPacketsPerSecond={overviewQuery.data.observedPacketsPerSecond}
                      metadata={overviewQuery.data.metadata}
                      scopeDescription={trendScopeDescription}
                      selectedRange={trendSelection}
                      selectedOverview={trendSelectionOverviewQuery.loading ? null : trendSelectionOverviewQuery.data}
                      selectionLoading={trendSelectionOverviewQuery.loading}
                      selectionError={trendSelectionOverviewQuery.error?.message ?? null}
                      onRangeSelect={selectTrendRange}
                      onClearRange={clearTrendSelection}
                    />
                  </section>
                </div>
                <TrafficComposition items={overviewQuery.data.topProtocols} totalBytes={overviewQuery.data.totalBytes} window={window} metadata={overviewQuery.data.metadata} scopeLabel={scopeLabel} />
              </div>
              <NetworkDimensions
                overview={overviewQuery.data}
                interfaces={interfacesQuery.data?.items ?? null}
                interfaceMeta={interfacesQuery.data?.meta ?? null}
                interfaceLoading={interfacesQuery.loading}
                interfaceError={interfacesQuery.error}
                onPreviousInterfacePage={() => setInterfaceCursor(interfacesQuery.data?.meta.prev ?? null)}
                onNextInterfacePage={() => setInterfaceCursor(interfacesQuery.data?.meta.next ?? null)}
              />
            </>
          ) : <QueryState {...overviewQuery} emptyMessage="No traffic overview is available for this interval." />}
        </section>

      </div>
    </OrgModuleLayout>
  );
};

export default FlowAnalyticsPage;
